import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import {
  Search,
  User,
  Clock,
  History,
  Lock,
  Unlock,
  QrCode,
  LogOut,
  Users,
  ShoppingBag,
  ArrowRightLeft,
  Trash2,
  Save,
  CreditCard,
  Menu,
  LayoutGrid,
  List,
  Plus,
  ChevronLeft,
  ChevronRight,
  PanelTop,
  PanelLeft,
} from "lucide-react";
import { useSidebarConfig } from "../contexts/SidebarConfigContext";
import StoreSelector from "../components/StoreSelector";
import NotificationDropdown from "../components/NotificationDropdown";
import ProductList from "../components/sales/ProductList";
import ProductGrid from "../components/sales/ProductGrid";
import CartSidebar from "../components/sales/CartSidebar";
import CategoryFilter from "../components/sales/CategoryFilter";
import SearchableSelect from "../components/SearchableSelect";
import SalesTypeModal from "../components/sales/SalesTypeModal";
import CustomerModal from "../components/sales/CustomerModal";
import EnhancedPaymentModal from "../components/sales/EnhancedPaymentModal";
import ShiftModal from "../components/sales/ShiftModal";
import PendingTransactionsModal from "../components/sales/PendingTransactionsModal";
import OptionSelectionModal from "../components/sales/OptionSelectionModal";
import SelectOpenBillModal from "../components/sales/SelectOpenBillModal";
import PreCheckoutHookModal from "../components/sales/PreCheckoutHookModal";
import SelectBestSellerModal from "../components/sales/SelectBestSellerModal";
import SelectTableModal from "../components/sales/SelectTableModal";
import BarcodeScannerModal from "../components/sales/BarcodeScannerModal";
import { Capacitor } from "@capacitor/core";
import { getStocks } from "../api/inventoryService";
import { conversionService } from "../api/conversionService";
import { getSellingUnitPayload, getSellingUnitKey } from "../utils/orderItemUnits";
import { executePrint, executePrintLabel, getLoggedInUserName } from "../utils/printManager";
import { filterLabelItems, getReceiptConfig } from "../utils/receiptConfig";
import { configService } from "../api/configService";
import { categoryService, salesTypeService, masterService } from "../api/masterService";
import {
  createSalesOrder,
  getSalesOrderDetail,
  getSalesOrders,
  updateSalesOrder,
  getBestSellerProducts,
  getBestSellerConfig,
  updateBestSellerConfig,
} from "../api/salesService";
import { authService } from "../api/authService";
import { useOrderCreation } from "../hooks/useSales";
import { useActiveShift, useShiftGuard } from "../hooks/useShift";
import { calculateOrderItemTotals, formatCurrency } from "../utils/salesUtils";
import { useNotification } from "../contexts/NotificationContext";

import { getScannerConfig } from "../utils/scannerConfig";

const ALL_CATEGORY = { id: "all", name: "Semua Kategori" };
const BEST_SELLER_CATEGORY = { id: "best_seller", name: "Best Seller", position: "after_all" };
const SALES_SCANNER_RUNTIME_KEY = "__asalinkSalesScannerRuntime";

function readProfileIdentity() {
  const userData = authService.getCurrentUser() || {};
  const email = userData?.email || localStorage.getItem("email") || "";
  const username =
    userData?.full_name ||
    userData?.name ||
    localStorage.getItem("username") ||
    email ||
    "User";
  const avatarUrl =
    localStorage.getItem("avatar_url") || userData?.avatar_url || null;
  return { username, email, avatarUrl };
}

export default function Sales() {
  const navigate = useNavigate();
  const { config: sidebarConfig, toggleMobile } = useSidebarConfig();
  const profileMenuRef = useRef(null);
  const profileButtonRef = useRef(null);
  const desktopProfileMenuRef = useRef(null);
  const desktopProfileButtonRef = useRef(null);
  const profilePanelRef = useRef(null);
  const [profileMenuCoords, setProfileMenuCoords] = useState({
    top: 0,
    right: 0,
  });
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem("sidebarCollapsed");
    return saved !== null ? JSON.parse(saved) : true;
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState(ALL_CATEGORY);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  // =========================================================================
  // [FLAG KHUSUS FE LEGIUN DIESEL] Sembunyikan Mode Grid dan Gambar Produk
  // Jika suatu saat ingin menampilkan kembali, cukup ubah nilai variabel ini menjadi false:
  // const HIDE_GRID_MODE = false;
  // const HIDE_PRODUCT_IMAGE = false;
  // =========================================================================
  const HIDE_GRID_MODE = true;
  const HIDE_PRODUCT_IMAGE = true;

  const [viewMode, setViewMode] = useState(() => {
    return localStorage.getItem("catalog_view_mode") || "list";
  });
  const [showListImage, setShowListImage] = useState(() => {
    const saved = localStorage.getItem("catalog_show_list_image");
    return saved !== null ? JSON.parse(saved) : true;
  });
  const [categoryPosition, setCategoryPosition] = useState(() => {
    return localStorage.getItem("catalog_category_position") || "top";
  });
  const [showProductStock, setShowProductStock] = useState(() => {
    const saved = localStorage.getItem("catalog_show_product_stock");
    return saved !== null ? JSON.parse(saved) : true;
  });
  const [profileIdentity, setProfileIdentity] = useState(() =>
    readProfileIdentity(),
  );

  // Product data state
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([ALL_CATEGORY]);
  const [bestSellerProducts, setBestSellerProducts] = useState([]);
  const [bestSellerConfig, setBestSellerConfig] = useState(null);
  const [isBestSellerModalOpen, setIsBestSellerModalOpen] = useState(false);
  const [productsLoading, setProductsLoading] = useState(true);
  const [productsError, setProductsError] = useState(null);
  const productsRequestRef = useRef(0);
  const categoryProductsCacheRef = useRef({});
  const [salesTypes, setSalesTypes] = useState([]);
  const [defaultSalesType, setDefaultSalesType] = useState(null);


  const { showSuccess, showError, showNotification } = useNotification();

  // Shift management with API integration
  const {
    canProcessTransaction,
    needsShiftOpen,
    activeShift,
    loading: shiftLoading,
    refreshActiveShift,
  } = useShiftGuard();

  // Use order creation hook
  const {
    cart,
    customer,
    sellerType: salesType,
    additionalCosts,
    discountTotal,
    totals,
    addItemToCart,
    updateItemQuantity,
    updateItemUnit,
    updateItemNotes,
    updateItemPrice,
    removeItemFromCart,
    clearOrderCart,
    replaceCart,
    setCustomer,
    setSellerType: setSalesType,
    setAdditionalCosts,
    setDiscountTotal,
    notes,
    setNotes,
    createOrder,
    loading: orderLoading,
    error: orderError,
  } = useOrderCreation();

  const initialCartDraft = (() => {
    try {
      const raw = localStorage.getItem("cart_pos_active_draft") || localStorage.getItem("pos_active_cart_draft");
      if (raw) return JSON.parse(raw);
    } catch {}
    return null;
  })();

  // Modals
  const [showSalesTypeModal, setShowSalesTypeModal] = useState(false);
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [showSelectTableModal, setShowSelectTableModal] = useState(false);
  const [selectedTable, setSelectedTable] = useState(() => initialCartDraft?.selectedTable || null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showShiftModal, setShowShiftModal] = useState(false);
  const [showPendingModal, setShowPendingModal] = useState(false);
  const [pendingOrder, setPendingOrder] = useState(() => initialCartDraft?.pendingOrder || null);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [showSelectOpenBillModal, setShowSelectOpenBillModal] = useState(false);
  const [activePendingOrders, setActivePendingOrders] = useState([]);
  const [loadingPendingOrders, setLoadingPendingOrders] = useState(false);

  // Pre-Checkout Hook States
  const [showPreCheckoutModal, setShowPreCheckoutModal] = useState(false);
  const [outOfStockItems, setOutOfStockItems] = useState([]);
  const [isAutoConversionEnabled, setIsAutoConversionEnabled] = useState(false);
  const [isCheckingStock, setIsCheckingStock] = useState(false);

  // Freetext nama pelanggan umum — TIDAK disimpan ke DB, hanya muncul di nota cetak
  const [walkInName, setWalkInName] = useState(() => initialCartDraft?.walkInName || "");

  // Unit kendaraan level transaksi (khas Legiun: 1 transaksi = 1 unit kendaraan)
  const [selectedUnit, setSelectedUnit] = useState(() => initialCartDraft?.selectedUnit || null);

  // Auto-sync / persist draft transaksi keranjang saat berpindah halaman
  useEffect(() => {
    const hasContent =
      (cart && cart.length > 0) ||
      pendingOrder ||
      (walkInName && walkInName.trim()) ||
      customer ||
      selectedTable ||
      selectedUnit ||
      (notes && notes.trim());

    if (hasContent) {
      const draftData = {
        cart,
        customer,
        walkInName,
        selectedTable,
        selectedUnit,
        salesType,
        notes,
        pendingOrder,
        additionalCosts,
        discountTotal,
      };
      try {
        localStorage.setItem("cart_pos_active_draft", JSON.stringify(draftData));
      } catch (e) {
        console.error("Gagal menyimpan draft cart:", e);
      }
    } else {
      try {
        localStorage.removeItem("cart_pos_active_draft");
        localStorage.removeItem("pos_active_cart_draft");
      } catch (e) {}
    }
  }, [cart, customer, walkInName, selectedTable, selectedUnit, salesType, notes, pendingOrder, additionalCosts, discountTotal]);

  // Scanner status state
  const [scannerConfig, setScannerConfigState] = useState(() => getScannerConfig());
  const [showCameraScanner, setShowCameraScanner] = useState(false);
  const activeScanner = (scannerConfig.savedDevices || []).find(
    (device) => device.id === scannerConfig.activeDeviceId || device.isDefault,
  );
  const activeScannerMode = activeScanner?.mode || scannerConfig.mode;
  const scannerReady = Boolean(activeScanner || scannerConfig.deviceName);
  const isApps = localStorage.getItem("PREVIEW_APPS") === "true";

  useEffect(() => {
    const handleScannerConfigUpdate = (e) => {
      setScannerConfigState(e?.detail || getScannerConfig());
    };
    window.addEventListener("scannerConfigUpdated", handleScannerConfigUpdate);
    window.addEventListener("storage", handleScannerConfigUpdate);
    return () => {
      window.removeEventListener("scannerConfigUpdated", handleScannerConfigUpdate);
      window.removeEventListener("storage", handleScannerConfigUpdate);
    };
  }, []);

  // Cart drawer state (mobile/tablet collapsible)
  // Di layar besar (lg) selalu terbuka via class lg:translate-x-0.
  // Di mobile/tablet default tertutup agar tidak menutupi layar.
  const [cartOpen, setCartOpen] = useState(false);

  // Date State
  const [currentDate, setCurrentDate] = useState(new Date());

  // Load categories, products, and sales types on mount
  const [optionModalOpen, setOptionModalOpen] = useState(false);
  const [optionProduct, setOptionProduct] = useState(null);

  useEffect(() => {
    const handleStorage = () => {
      const savedMode = localStorage.getItem("catalog_view_mode");
      if (savedMode) setViewMode(savedMode);
      const savedImg = localStorage.getItem("catalog_show_list_image");
      if (savedImg !== null) setShowListImage(JSON.parse(savedImg));
      const savedStock = localStorage.getItem("catalog_show_product_stock");
      if (savedStock !== null) setShowProductStock(JSON.parse(savedStock));
    };
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []);

  useEffect(() => {
    fetchCategories();
    fetchProducts();
    fetchSalesTypes();
    fetchPendingCount();
  }, []);

  useEffect(() => {
    const timer = setInterval(() => setCurrentDate(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const syncProfile = () => {
      setProfileIdentity(readProfileIdentity());
    };

    syncProfile();

    const handleAvatarUpdate = (e) => {
      if (e?.detail?.avatarUrl) {
        localStorage.setItem("avatar_url", e.detail.avatarUrl);
      }
      syncProfile();
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        syncProfile();
        // Refresh stok produk juga saat tab kembali aktif (realtime)
        if (selectedCategory.id === "best_seller") {
          fetchBestSellerProducts(searchQuery);
        } else {
          fetchProducts(selectedCategory.id, searchQuery);
        }
      }
    };
    const handleWindowFocus = () => {
      syncProfile();
      // Refresh stok produk saat window kembali fokus (realtime)
      if (selectedCategory.id === "best_seller") {
        fetchBestSellerProducts(searchQuery);
      } else {
        fetchProducts(selectedCategory.id, searchQuery);
      }
    };

    window.addEventListener("avatarUpdated", handleAvatarUpdate);
    window.addEventListener("storage", syncProfile);
    window.addEventListener("focus", handleWindowFocus);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      window.removeEventListener("avatarUpdated", handleAvatarUpdate);
      window.removeEventListener("storage", syncProfile);
      window.removeEventListener("focus", handleWindowFocus);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory.id, searchQuery]);

  useEffect(() => {
    const handleOutsideClick = (e) => {
      const insideButton =
        (profileMenuRef.current && profileMenuRef.current.contains(e.target)) ||
        (desktopProfileMenuRef.current && desktopProfileMenuRef.current.contains(e.target));
      const insidePanel =
        profilePanelRef.current && profilePanelRef.current.contains(e.target);
      if (!insideButton && !insidePanel) {
        setShowProfileMenu(false);
      }
    };
    const handleKeyDown = (e) => {
      if (e.key === "Escape") setShowProfileMenu(false);
    };

    document.addEventListener("mousedown", handleOutsideClick);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  const getVisibleProfileButtonRect = () => {
    const mobileRect = profileButtonRef.current?.getBoundingClientRect();
    const desktopRect = desktopProfileButtonRef.current?.getBoundingClientRect();
    return mobileRect && mobileRect.width > 0 ? mobileRect : desktopRect;
  };

  useEffect(() => {
    if (!showProfileMenu) return;
    const updateCoords = () => {
      const rect = getVisibleProfileButtonRect();
      if (!rect) return;
      setProfileMenuCoords({
        top: rect.bottom + 8,
        right: window.innerWidth - rect.right,
      });
    };
    updateCoords();
    window.addEventListener("resize", updateCoords);
    window.addEventListener("scroll", updateCoords, true);
    return () => {
      window.removeEventListener("resize", updateCoords);
      window.removeEventListener("scroll", updateCoords, true);
    };
  }, [showProfileMenu]);

  const handleProfileToggle = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    if (rect && rect.width > 0) {
      setProfileMenuCoords({
        top: rect.bottom + 8,
        right: window.innerWidth - rect.right,
      });
    }
    setShowProfileMenu((prev) => !prev);
  };

  const handleProfileClick = () => {
    setShowProfileMenu(false);
    navigate("/profile");
  };

  const handleLogout = async () => {
    try {
      await authService.logout();
      navigate("/login", { replace: true });
    } catch (error) {
      console.error("Logout error:", error);
      navigate("/login", { replace: true });
    }
  };

  const fetchCategories = async () => {
    try {
      const [catRes, configRes] = await Promise.all([
        categoryService.list({ limit: 100, has_product: true }).catch(() => null),
        getBestSellerConfig().catch(() => null)
      ]);
      
      const configData = configRes?.data;
      if (configData) {
        setBestSellerConfig(configData);
      }

      const catData = catRes?.data || [];
      const newCategories = [ALL_CATEGORY];
      
      if (configData?.enabled) {
        newCategories.push(BEST_SELLER_CATEGORY);
      }
      
      newCategories.push(...catData.map((c) => ({ id: c.id, name: c.name })));
      
      setCategories(newCategories);
    } catch {
      // categories fallback: keep default
    }
  };

  const fetchBestSellerProducts = async (query = searchQuery, isBackground = false) => {
    const cacheKey = `best_seller_${query || ""}`;
    const cachedData = categoryProductsCacheRef.current[cacheKey];

    if (cachedData && !isBackground) {
      setProducts(cachedData);
    } else if (!cachedData && !isBackground) {
      setProductsLoading(true);
    }

    try {
      const res = await getBestSellerProducts({ limit: 50, query });
      const data = res?.data || [];
      const bestSellerIds = [...new Set(data.map(item => item.product_id))];
      if (bestSellerIds.length > 0) {
        const stockRes = await getStocks({ limit: 1000, use_service: true, status: "ACTIVE", product_ids: bestSellerIds.join(",") });
        if (stockRes?.data) {
          const isApps = localStorage.getItem("PREVIEW_APPS") === "true";
          const formatted = stockRes.data
            .filter((stock) => !stock.option_id)
            .filter((stock) => !isApps || (!stock.brand_name && !stock.brand_id))
            .filter((stock) => {
              const st = String(stock.status || stock.product_status || "ACTIVE").toUpperCase();
              return st === "ACTIVE" || st === "AKTIF";
            })
            .map((stock) => ({
              id: stock.id,
              product_id: stock.item_type === "SERVICE" ? null : stock.product_id,
              service_id: stock.service_id || (stock.item_type === "SERVICE" ? (stock.service_id || stock.product_id || stock.id) : null),
              item_type: stock.item_type || (stock.service_id ? "SERVICE" : "PRODUCT"),
              category_id: stock.category_id || stock.product_category_id || stock.category || null,
              category_name: stock.category_name || (typeof stock.category === "string" ? stock.category : null) || null,
              category: stock.category_name || stock.category || null,
              name: stock.product_name || stock.name,
              code: stock.product_code || "",
              variant_id: stock.variant_id || null,
              variant_name: stock.variant_name || null,
              brand_id: stock.brand_id || null,
              brand_name: stock.brand_name || null,
              options: stock.options || [],
              price: stock.unit_price || stock.price || 0,
              sell_price: stock.price || stock.unit_price || 0,
              selling_prices: stock.selling_prices || stock.product_selling_prices || stock.product?.selling_prices || [],
              has_addons: Boolean(stock.has_addons || (stock.addon_groups && stock.addon_groups.length > 0)),
              addon_groups: stock.addon_groups || [],
              image_url: stock.image_url,
              stock: stock.current_stock ?? stock.current_qty ?? stock.quantity ?? 0,
              unit: stock.unit_name || stock.unit || "Pcs",
            }));

          categoryProductsCacheRef.current[cacheKey] = formatted;
          setProducts(formatted);
        } else {
          categoryProductsCacheRef.current[cacheKey] = [];
          setProducts([]);
        }
      } else {
        categoryProductsCacheRef.current[cacheKey] = [];
        setProducts([]);
      }
    } catch (error) {
      console.error("Error fetching best seller products:", error);
    } finally {
      setProductsLoading(false);
    }
  };

  const handleAddManualBestSeller = async (productId) => {
    try {
      const newIds = [...(bestSellerConfig?.manual_product_ids || []), productId];
      const newConfig = { ...bestSellerConfig, manual_product_ids: newIds };
      await updateBestSellerConfig(newConfig);
      setBestSellerConfig(newConfig);
      
      await fetchBestSellerProducts(searchQuery);
      
      showSuccess("Produk ditambahkan ke Best Seller");
    } catch (error) {
      console.error(error);
      showError("Gagal menambahkan ke Best Seller");
    }
  };

  const handleRemoveManualBestSeller = async (productId) => {
    try {
      const newIds = (bestSellerConfig?.manual_product_ids || []).filter(id => id !== productId);
      const newConfig = { ...bestSellerConfig, manual_product_ids: newIds };
      await updateBestSellerConfig(newConfig);
      setBestSellerConfig(newConfig);
      
      await fetchBestSellerProducts(searchQuery);
      
      showSuccess("Produk dihapus dari Best Seller");
    } catch (error) {
      console.error(error);
      showError("Gagal menghapus dari Best Seller");
    }
  };

  const fetchSalesTypes = async () => {
    try {
      const [res, storeRes] = await Promise.all([
        salesTypeService.list({ limit: 100 }),
        configService.getStore().catch(() => null),
      ]);
      const data = res?.data || [];
      const mappedTypes = data
        .filter((st) => st.status === "ACTIVE")
        .map((st) => ({
          id: st.id,
          name: st.name,
          slug: st.slug || st.code || st.type,
          description: st.description,
          jenis: st.jenis,
          satuan: st.satuan,
          nilai: st.nilai,
        }));
      setSalesTypes(mappedTypes);

      const store = storeRes?.data || storeRes || {};
      const configuredId = store?.settings?.default_sales_type_id || localStorage.getItem("default_sales_type_id");
      const configuredType = mappedTypes.find(
        (type) => String(type.id) === String(configuredId),
      );
      const regulerType = mappedTypes.find(
        (t) =>
          t.name?.toUpperCase() === "REGULAR" ||
          t.name?.toUpperCase() === "REGULER" ||
          t.slug?.toUpperCase() === "REGULAR" ||
          t.slug?.toUpperCase() === "REGULER" ||
          t.description?.toLowerCase()?.includes("default") ||
          (t.jenis === "Normal" && t.name?.toLowerCase()?.includes("reg"))
      );
      const internalType = mappedTypes.find(
        (t) => t.slug?.toUpperCase() === "INTERNAL" || t.name?.toUpperCase() === "INTERNAL",
      );

      const resolvedDefault = configuredType || regulerType || internalType || mappedTypes[0];
      if (resolvedDefault) {
        setDefaultSalesType(resolvedDefault);
        
        // Cek apakah kasir sedang memiliki transaksi aktif tersimpan di draft
        const hasActiveCustomDraft = Boolean(
          initialCartDraft?.salesType && (
            (initialCartDraft?.cart && initialCartDraft.cart.length > 0) ||
            initialCartDraft?.pendingOrder
          )
        );

        if (hasActiveCustomDraft) {
          const draftType = initialCartDraft.salesType;
          const matched = mappedTypes.find(
            (t) =>
              (draftType.id && String(t.id) === String(draftType.id)) ||
              (draftType.slug && String(t.slug).toUpperCase() === String(draftType.slug).toUpperCase()) ||
              (draftType.name && String(t.name).toLowerCase() === String(draftType.name).toLowerCase())
          );
          setSalesType(matched || resolvedDefault);
        } else {
          // Ketika kasir membuka halaman penjualan baru, selalu gunakan tipe penjualan default yang dikonfigurasi
          setSalesType(resolvedDefault);
        }
      }
    } catch {
      // sales types fallback: keep defaults
      const fallbackTypes = [
        { id: "reguler", name: "Reguler", slug: "REGULER" },
        { id: "internal", name: "Internal", slug: "INTERNAL" },
        { id: "external", name: "Umum", slug: "EXTERNAL" },
        { id: "online", name: "Online", slug: "ONLINE" },
      ];
      setSalesTypes(fallbackTypes);
      setDefaultSalesType(fallbackTypes[0]);
      setSalesType((prev) => prev || fallbackTypes[0]);
    }
  };

  const fetchProducts = async (
    categoryId = selectedCategory.id,
    query = searchQuery,
    isBackground = false,
  ) => {
    const requestId = ++productsRequestRef.current;
    const cacheKey = `${categoryId}_${query || ""}`;
    const cachedData = categoryProductsCacheRef.current[cacheKey];

    if (cachedData && !isBackground) {
      setProducts(cachedData);
    } else if (!cachedData && !isBackground) {
      setProductsLoading(true);
    }
    setProductsError(null);

    try {
      const params = { limit: 1000, use_service: true, status: "ACTIVE" };
      if (query) params.query = query;
      if (categoryId && categoryId !== "all") params.category_id = categoryId;

      const response = await getStocks(params);

      if (requestId !== productsRequestRef.current) return;

      if (response?.data) {
        const isApps = localStorage.getItem("PREVIEW_APPS") === "true";
        const formatted = response.data
          .filter((stock) => !stock.option_id)
          .filter((stock) => !isApps || (!stock.brand_name && !stock.brand_id))
          .filter((stock) => {
            const st = String(stock.status || stock.product_status || "ACTIVE").toUpperCase();
            return st === "ACTIVE" || st === "AKTIF";
          })
          .map((stock) => ({
            id: stock.id,
            product_id: stock.item_type === "SERVICE" ? null : stock.product_id,
            service_id: stock.service_id || (stock.item_type === "SERVICE" ? (stock.service_id || stock.product_id || stock.id) : null),
            item_type: stock.item_type || (stock.service_id ? "SERVICE" : "PRODUCT"),
            category_id: stock.category_id || stock.product_category_id || stock.category || null,
            category_name: stock.category_name || (typeof stock.category === "string" ? stock.category : null) || null,
            category: stock.category_name || stock.category || null,
            name: stock.product_name || stock.name,
            code: stock.product_code || "",
            barcode: stock.barcode || stock.product_barcode || stock.sku || stock.code || stock.product_code || "",
            sku: stock.sku || stock.variant_code || stock.code || stock.product_code || "",
            variant_id: stock.variant_id || null,
            variant_name: stock.variant_name || null,
            variants: stock.variants || stock.product_variants || [],
            brand_id: stock.brand_id || null,
            brand_name: stock.brand_name || null,
            options: stock.options || [],
            price: stock.unit_price || stock.price || 0,
            sell_price: stock.price || stock.unit_price || 0,
            selling_prices: stock.selling_prices || stock.product_selling_prices || stock.product?.selling_prices || [],
            has_addons: Boolean(stock.has_addons || (stock.addon_groups && stock.addon_groups.length > 0)),
            addon_groups: stock.addon_groups || [],
            image_url: stock.image_url,
            stock:
              stock.current_stock ?? stock.current_qty ?? stock.quantity ?? 0,
            unit: stock.unit_name || stock.unit || "Pcs",
          }));

        categoryProductsCacheRef.current[cacheKey] = formatted;
        setProducts(formatted);
      }
    } catch (error) {
      if (requestId !== productsRequestRef.current) return;
      console.error("Error fetching products:", error);
      if (!cachedData) {
        setProductsError("Gagal memuat data produk");
        showError("Gagal memuat data produk");
      }
    } finally {
      if (requestId === productsRequestRef.current) {
        setProductsLoading(false);
      }
    }
  };

  // Re-fetch when search changes (debounced)
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      const isBS = selectedCategory.id === "best_seller";
      const cacheKey = isBS ? `best_seller_${searchQuery || ""}` : `${selectedCategory.id}_${searchQuery || ""}`;
      const hasCache = !!categoryProductsCacheRef.current[cacheKey];

      if (isBS) {
        fetchBestSellerProducts(searchQuery, hasCache);
      } else {
        fetchProducts(selectedCategory.id, searchQuery, hasCache);
      }
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [searchQuery, selectedCategory.id]);

  const formatDate = (date) => {
    const options = {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    };
    return date.toLocaleDateString("id-ID", options);
  };

  const formatTime = (date) => {
    return (
      date.toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit" }) +
      " WIB"
    );
  };

  const handleSelectTransactionUnit = (unit) => {
    setSelectedUnit(unit);
    if (cart && cart.length > 0) {
      cart.forEach((_, idx) => {
        updateItemUnit(idx, unit);
      });
    }
  };

  const handleClearTransactionUnit = () => {
    setSelectedUnit(null);
    if (cart && cart.length > 0) {
      cart.forEach((_, idx) => {
        updateItemUnit(idx, null);
      });
    }
  };

  const handleAddToCart = (product) => {
    // Check if shift is active before adding to cart
    if (!canProcessTransaction) {
      showError(
        "Untuk menambahkan produk, silahkan buka shift kasir terlebih dulu",
      );
      return;
    }

    const qty = Math.max(1, Number(product.qty) || 1);
    const unitFactor = Number(product.unit_factor || product.factor || 1) || 1;
    const isServiceItem = product.item_type === "SERVICE" || !!product.service_id;
    const cartItem = {
      id: `cart-${product.service_id || product.product_id || product.id}-${product.variant_id || "base"}-${product.selected_options?.map(o => o.id).join("_") || "opt"}-${product.unit_id || "baseunit"}-${Date.now()}`,
      item_type: isServiceItem ? "SERVICE" : "PRODUCT",
      product_id: isServiceItem ? null : (product.product_id || product.id),
      service_id: isServiceItem ? (product.service_id || product.product_id || product.id) : null,
      product_variant_id: product.variant_id || null,
      selected_options: product.selected_options || [],
      base_price: product.base_price || product.price,
      product_name: product.name || product.product_name,
      item_name: product.variant_name
        ? `${product.name} - ${product.variant_name}`
        : product.name,
      variant_name: product.variant_name || null,
      variant_only_name: product.variant_only_name || null,
      option_names: product.option_names || [],
      item_code: product.code || (isServiceItem ? `SRV-${product.service_id || product.id}` : `PRD-${product.product_id || product.id}`),
      quantity: qty,
      unit_price: product.price,
      selling_prices: product.selling_prices || product.product?.selling_prices || product._group?.variants?.[0]?.selling_prices || [],
      base_sell_price: product.sell_price !== undefined ? product.sell_price : (product.price || 0),
      satuan: product.unit_name || product.unit || product.selling_unit_name || product.satuan || null,
      selling_unit_id: isServiceItem ? null : (product.selling_unit_id || product.unit_id || null),
      selling_unit_name: isServiceItem ? null : (product.selling_unit_name || product.unit_name || product.satuan || null),
      unit_id: selectedUnit?.id || product.asset_unit_id || null,
      unit: selectedUnit
        ? { id: selectedUnit.id, name: selectedUnit.name || selectedUnit.unit_name }
        : (product.asset_unit_name ? { id: product.asset_unit_id, name: product.asset_unit_name } : null),
      unit_name: selectedUnit?.name || selectedUnit?.unit_name || product.asset_unit_name || null,
      unit_factor: unitFactor,
      category_id: product.category_id || product.category || product._group?.variants?.[0]?.category_id || null,
      notes: "",
    };

    addItemToCart(cartItem);
    // showSuccess('Produk ditambahkan', `${product.name} berhasil ditambahkan ke keranjang`);
  };

  const triggerAddToCart = async (product) => {
    const isApps = localStorage.getItem("PREVIEW_APPS") === "true";
    const groupVariants = (product.variants || []).filter(
      (v) => !isApps || (!v.brand_name && !v.brand_id)
    );
    const hasRealVariants = groupVariants.some(
      (v) => v.variant_id != null || (v.variant_name && v.variant_name !== "DEFAULT")
    );
    const hasOptions = (product.options && product.options.length > 0) ||
                       (product.addon_groups && product.addon_groups.length > 0) ||
                       (product.addons && product.addons.length > 0) ||
                       product.has_addons === true;

    if (hasRealVariants || hasOptions) {
      setOptionProduct(product);
      setOptionModalOpen(true);
      return;
    }

    // Tanpa varian di list: cek detail produk & satuan jual konversi
    // agar produk yang punya addon/extra atau konversi satuan tetap membuka modal opsi.
    const productId = product.product_id || product.id;
    if (productId && product.item_type !== "SERVICE") {
      try {
        const [prodRes, convRes] = await Promise.allSettled([
          productService.get(productId),
          conversionService.rules.list({ product_id: productId }),
        ]);

        let hasAddonsOrVars = false;
        let enrichedProduct = product;

        if (prodRes.status === "fulfilled") {
          const fullP = prodRes.value?.data || prodRes.value;
          const fullAddonGroups = fullP?.addon_groups || [];
          const fullVars = (fullP?.variants || [])
            .filter((v) => !v.unit_id || v.unit_id === fullP?.unit_id)
            .filter((v) => !isApps || !v.brand_id)
            .filter((v) => {
              const vid = "variant_id" in v ? v.variant_id : v.id;
              const vname = "variant_name" in v ? v.variant_name : v.name;
              return vid != null || (vname && vname !== "DEFAULT");
            });

          if (fullAddonGroups.length > 0 || fullVars.length > 0) {
            hasAddonsOrVars = true;
            enrichedProduct = { ...product, ...fullP, addon_groups: fullAddonGroups };
          }
        }

        let hasExtraUnits = false;
        if (convRes.status === "fulfilled") {
          const rules = convRes.value?.data?.items || convRes.value?.data?.data || convRes.value?.data || [];
          if (Array.isArray(rules) && rules.length > 0) {
            hasExtraUnits = true;
          }
        }

        if (hasAddonsOrVars || hasExtraUnits) {
          setOptionProduct(enrichedProduct);
          setOptionModalOpen(true);
          return;
        }
      } catch (err) {
        console.warn("Failed to fetch product addons/conversion rules:", err?.message);
      }
    }

    handleAddToCart(product);
  };

  const handleBarcodeScan = React.useCallback(async (scannedCode) => {
    const rawCode = String(scannedCode || "").replace(/[\u0000-\u001F\u007F]/g, "").trim();
    const cleanCode = rawCode.toLowerCase();
    console.log("[Sales] handleBarcodeScan called →", JSON.stringify(rawCode), "canProcessTransaction:", canProcessTransaction, "products count:", products.length);
    if (!cleanCode) return;

    if (!canProcessTransaction) {
      showError("Silakan buka shift kasir terlebih dulu untuk melakukan transaksi scan.");
      return;
    }

    const scannerSettings = getScannerConfig();
    if (scannerSettings.autoAddToCart === false) {
      showNotification("info", "Scanner aktif, tetapi tambah otomatis ke keranjang sedang dimatikan.");
      return;
    }

    // Kunci dipasang sebelum pencarian API agar dua pemicu bersamaan
    // dari satu scan fisik tidak menambah kuantitas dua kali.
    const scanLock = { startedAt: Date.now() };
    const previousScan = _scanInFlightRef.current.get(cleanCode);
    if (
      previousScan &&
      (previousScan.processing || scanLock.startedAt - previousScan.startedAt < 1500)
    ) {
      console.log("[Sales] Blocked duplicate scan in flight:", JSON.stringify(cleanCode));
      return;
    }
    _scanInFlightRef.current.set(cleanCode, { ...scanLock, processing: true });

    try {
      // Dedup guard level-2: block the same code that was successfully added within 1.5s,
    // regardless of which trigger (fireScan, fallback timer, re-attached listener) fired it.
    if (
      _lastAddedScanRef.current.code === cleanCode &&
      Date.now() - _lastAddedScanRef.current.time < 1500
    ) {
      console.log("[Sales] Blocked duplicate add (handled within 1.5s):", JSON.stringify(cleanCode));
      return;
    }
    const markAdded = (code) => {
      _lastAddedScanRef.current = { code: String(code).toLowerCase(), time: Date.now() };
    };

    let matchedItem = null;
    let matchedVariant = null;

    // Helper comparison function (handles exact match + leading zero strip match)
    const matchesCode = (val) => {
      if (!val) return false;
      const str = String(val).trim().toLowerCase();
      if (str === cleanCode) return true;
      const strNoZero = str.replace(/^0+/, "");
      const cleanNoZero = cleanCode.replace(/^0+/, "");
      return strNoZero.length > 0 && strNoZero === cleanNoZero;
    };

    // 1. Search in current loaded products
    for (const p of products) {
      const pCode = p.code;
      const pBarcode = p.barcode;
      const pSku = p.sku;
      const pId = p.product_id || p.id;
      const pVarId = p.variant_id;

      // Check if p itself is the exact matched variant or product
      if (
        matchesCode(pCode) ||
        matchesCode(pBarcode) ||
        matchesCode(pSku) ||
        matchesCode(pId) ||
        matchesCode(pVarId)
      ) {
        matchedItem = p;
        if (p.variant_id) {
          matchedVariant = {
            id: p.variant_id,
            variant_id: p.variant_id,
            name: p.variant_name || p.name,
            variant_name: p.variant_name,
            price: p.price,
            barcode: p.barcode,
          };
        }
        break;
      }

      // Check inside p.variants array
      if (p.variants && Array.isArray(p.variants)) {
        const foundVar = p.variants.find((v) => {
          return (
            matchesCode(v.barcode) ||
            matchesCode(v.product_barcode) ||
            matchesCode(v.variant_barcode) ||
            matchesCode(v.sku) ||
            matchesCode(v.variant_code) ||
            matchesCode(v.code) ||
            matchesCode(v.variant_id) ||
            matchesCode(v.id)
          );
        });

        if (foundVar) {
          matchedItem = p;
          matchedVariant = foundVar;
          break;
        }
      }
    }

    // 2. Cek barcode satuan jual (aturan konversi) via API — exact match.
    // Barcode dus/lusin terdaftar di master_unit_conversions.barcode.
    if (!matchedItem) {
      try {
        const scanRes = await conversionService.rules.scanBarcode(rawCode);
        const rule = scanRes?.data || scanRes || null;
        if (rule && rule.product_id) {
          handleAddToCart({
            product_id: rule.product_id,
            id: rule.product_id,
            name: rule.product_name,
            code: "",
            barcode: rawCode,
            variant_id: rule.variant_id || null,
            variant_name: rule.variant_name || null,
            price: rule.sell_price || 0,
            unit_id: rule.from_unit_id,
            unit: rule.from_unit_name,
            unit_name: rule.from_unit_name,
          });
          markAdded(cleanCode);
          showSuccess(`Barcode cocok: ${rule.product_name}${rule.variant_name ? ` - ${rule.variant_name}` : ""} (${rule.from_unit_name})`);
          return;
        }
      } catch (e) {
        // Bukan barcode satuan (404) atau error jaringan — lanjut ke fallback stok.
        console.debug("[Sales] Conversion barcode scan miss:", e?.message);
      }
    }

    // 3. Query DB stock if not in memory list
    if (!matchedItem) {
      try {
        const stockRes = await getStocks({ limit: 50, use_service: true, status: "ACTIVE", query: rawCode });
        if (stockRes?.data && stockRes.data.length > 0) {
          const s = stockRes.data.find((st) => {
            return (
              matchesCode(st.product_code) ||
              matchesCode(st.code) ||
              matchesCode(st.barcode) ||
              matchesCode(st.product_barcode) ||
              matchesCode(st.variant_barcode) ||
              matchesCode(st.sku) ||
              matchesCode(st.variant_sku) ||
              matchesCode(st.product_id) ||
              matchesCode(st.id) ||
              matchesCode(st.variant_id)
            );
          }) || stockRes.data[0];

          matchedItem = {
            id: s.id,
            product_id: s.product_id,
            service_id: s.service_id,
            item_type: s.item_type || "PRODUCT",
            name: s.product_name || s.name,
            code: s.product_code || "",
            barcode: s.barcode || s.product_barcode || s.variant_barcode || s.sku || s.code || "",
            sku: s.sku || s.variant_sku || s.variant_code || s.code || "",
            variant_id: s.variant_id || null,
            variant_name: s.variant_name || null,
            variants: s.variants || s.product_variants || [],
            options: s.options || [],
            price: s.unit_price || s.price || 0,
            stock: s.current_stock ?? s.current_qty ?? 0,
            unit: s.unit_name || s.unit || "Pcs",
          };
          if (s.variant_id) {
            matchedVariant = {
              id: s.variant_id,
              variant_id: s.variant_id,
              name: s.variant_name || s.product_name || s.name,
              variant_name: s.variant_name,
              price: s.unit_price || s.price || 0,
              barcode: s.barcode,
            };
          }
        }
      } catch (e) {
        console.error("Stock lookup scan error:", e);
      }
    }

    if (!matchedItem) {
      showError(`Produk/Varian dengan barcode "${rawCode}" tidak ditemukan.`);
      return;
    }

    // 3. Directly add matched variant or item to cart
    if (matchedVariant) {
      const targetVariantId = matchedVariant.variant_id || matchedVariant.id;
      const targetVariantName = matchedVariant.variant_name || matchedVariant.name || matchedVariant.only_name;
      handleAddToCart({
        ...matchedItem,
        variant_id: targetVariantId,
        variant_name: targetVariantName,
        price: matchedVariant.price || matchedVariant.unit_price || matchedItem.price,
      });
      markAdded(cleanCode);
      showSuccess(`Barcode cocok: ${matchedItem.name} (${targetVariantName})`);
      return;
    }

    // If matchedItem itself is a specific variant stock row
    if (matchedItem.variant_id) {
      handleAddToCart(matchedItem);
      markAdded(cleanCode);
      showSuccess(`Barcode cocok: ${matchedItem.name} ${matchedItem.variant_name ? `(${matchedItem.variant_name})` : ""}`);
      return;
    }

    // If base product has exactly 1 variant
    const variantsList = matchedItem.variants || [];
    const addedProductKey = [
      matchedItem.product_id || matchedItem.id,
      matchedVariant?.variant_id || matchedVariant?.id || matchedItem.variant_id || "base",
    ].join("|");
    const lastAddedProduct = _lastAddedProductRef.current;
    if (
      lastAddedProduct.key === addedProductKey &&
      Date.now() - lastAddedProduct.time < 1500
    ) {
      console.log("[Sales] Blocked duplicate product add:", addedProductKey);
      return;
    }
    _lastAddedProductRef.current = {
      key: addedProductKey,
      time: Date.now(),
    };

    if (variantsList.length === 1) {
      const singleVar = variantsList[0];
      handleAddToCart({
        ...matchedItem,
        variant_id: singleVar.variant_id || singleVar.id,
        variant_name: singleVar.variant_name || singleVar.name,
        price: singleVar.price || matchedItem.price,
      });
      markAdded(cleanCode);
      showSuccess(`Barcode cocok: ${matchedItem.name} (${singleVar.variant_name || "Default"})`);
      return;
    }

      // If base product has multiple variants and no specific variant was scanned, open modal
      triggerAddToCart(matchedItem);
      showSuccess(`Barcode cocok: ${matchedItem.name}`);
    } finally {
      if (_scanInFlightRef.current.get(cleanCode)?.startedAt === scanLock.startedAt) {
        _scanInFlightRef.current.delete(cleanCode);
      }
    }
  }, [products, canProcessTransaction, handleAddToCart, triggerAddToCart, showError, showSuccess, showNotification]);

  const handleOpenScanner = () => {
    if (!scannerReady) {
      navigate("/konfigurasi");
      return;
    }

    if (activeScannerMode === "capacitor_apk") {
      if (!canProcessTransaction) {
        showError("Silakan buka shift kasir terlebih dulu untuk melakukan scan.");
        return;
      }
      setShowCameraScanner(true);
      return;
    }

    showSuccess("Scanner siap digunakan. Arahkan scanner fisik ke barcode produk.");
  };

  // Activate background barcode scanner listener — inline for reliability
  // USB HID / Bluetooth HID scanner mengirim karakter cepat diakhiri Enter/Tab
  const _scanBufferRef = useRef("");
  const _scanLastTimeRef = useRef(0);
  const _scanTimerRef = useRef(null);
  const _scanInFlightRef = useRef(new Map());
  const _lastFiredScanRef = useRef({ code: "", time: 0 });
  const _lastAddedScanRef = useRef({ code: "", time: 0 });
  const _lastAddedProductRef = useRef({ key: "", time: 0 });
  const _scanSequenceRef = useRef(0);
  const _scanFiredSequenceRef = useRef(0);

  // Stable ref for handleBarcodeScan — prevents listener re-attachment on every render
  const _handleBarcodeScanRef = useRef(handleBarcodeScan);
  useEffect(() => {
    _handleBarcodeScanRef.current = handleBarcodeScan;
  }, [handleBarcodeScan]);

  useEffect(() => {
    const previousRuntime = window[SALES_SCANNER_RUNTIME_KEY];
    previousRuntime?.dispose?.();

    const resetBuffer = () => {
      if (_scanTimerRef.current) {
        clearTimeout(_scanTimerRef.current);
        _scanTimerRef.current = null;
      }
      _scanBufferRef.current = "";
      _scanLastTimeRef.current = 0;
    };

    const fireScan = (code, sequenceId) => {
      const config = getScannerConfig();
      const clean = String(code || "").replace(/[\u0000-\u001F\u007F]/g, "").trim();
      if (!clean || clean.length < (config.minBarcodeLength || 3)) return;

      // Satu rangkaian keyboard hanya boleh menghasilkan satu dispatch.
      if (sequenceId && _scanFiredSequenceRef.current === sequenceId) {
        console.log("[Sales] Blocked duplicate scanner sequence:", sequenceId);
        return;
      }
      if (sequenceId) _scanFiredSequenceRef.current = sequenceId;

      const now = Date.now();
      // Tetap tahan event identik dari listener atau perangkat yang mengirim suffix ganda.
      if (
        _lastFiredScanRef.current.code.toLowerCase() === clean.toLowerCase() &&
        now - _lastFiredScanRef.current.time < 1000
      ) {
        console.log("[Sales] Blocked duplicate scanner dispatch:", JSON.stringify(clean));
        return;
      }
      _lastFiredScanRef.current = { code: clean, time: now };

      if (config.beepOnScan) {
        try {
          const ctx = new (window.AudioContext || window.webkitAudioContext)();
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.type = "sine";
          osc.frequency.setValueAtTime(1400, ctx.currentTime);
          osc.frequency.exponentialRampToValueAtTime(800, ctx.currentTime + 0.08);
          gain.gain.setValueAtTime(0.15, ctx.currentTime);
          gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.08);
          osc.connect(gain);
          gain.connect(ctx.destination);
          osc.start();
          osc.stop(ctx.currentTime + 0.08);
        } catch (_) {}
      }
      _handleBarcodeScanRef.current(clean);
    };

    const onKey = (e) => {
      if (e.repeat || e.isComposing) return;

      const now = Date.now();
      const config = getScannerConfig();
      const minLength = config.minBarcodeLength || 3;
      const isEnter =
        e.key === "Enter" ||
        e.key === "Tab" ||
        e.keyCode === 13 ||
        e.code === "NumpadEnter" ||
        e.code === "Enter";

      if (_scanTimerRef.current) {
        clearTimeout(_scanTimerRef.current);
        _scanTimerRef.current = null;
      }

      // Enter atau Tab menutup satu sequence scan.
      if (isEnter) {
        const code = _scanBufferRef.current.trim();
        const sequenceId = _scanSequenceRef.current;
        if (code.length >= minLength) {
          e.preventDefault();
          e.stopPropagation();
          fireScan(code, sequenceId);
        }
        resetBuffer();
        return;
      }

      // Ambil karakter yang dikirim scanner HID.
      let char = "";
      if (e.key && e.key.length === 1) {
        char = e.key;
      } else if (e.keyCode >= 48 && e.keyCode <= 90) {
        char = String.fromCharCode(e.keyCode);
        if (!e.shiftKey) char = char.toLowerCase();
      } else if (e.keyCode >= 96 && e.keyCode <= 105) {
        char = String(e.keyCode - 96);
      }

      if (!char) return;

      const timeDiff = _scanLastTimeRef.current > 0 ? now - _scanLastTimeRef.current : 0;
      if (timeDiff > 150 && _scanBufferRef.current.length > 0) {
        resetBuffer();
      }
      if (_scanBufferRef.current.length === 0) {
        _scanSequenceRef.current += 1;
      }

      _scanLastTimeRef.current = now;
      _scanBufferRef.current += char;

      // Fallback untuk scanner yang tidak mengirim Enter atau Tab.
      if (_scanBufferRef.current.length >= minLength) {
        const sequenceId = _scanSequenceRef.current;
        _scanTimerRef.current = setTimeout(() => {
          if (sequenceId !== _scanSequenceRef.current) return;
          const fallbackCode = _scanBufferRef.current.trim();
          if (fallbackCode.length >= minLength) {
            fireScan(fallbackCode, sequenceId);
          }
          resetBuffer();
        }, 200);
      }
    };

    document.addEventListener("keydown", onKey, true);

    const runtime = {
      dispose: () => {
        resetBuffer();
        document.removeEventListener("keydown", onKey, true);
        if (window[SALES_SCANNER_RUNTIME_KEY] === runtime) {
          delete window[SALES_SCANNER_RUNTIME_KEY];
        }
      },
    };
    window[SALES_SCANNER_RUNTIME_KEY] = runtime;

    return runtime.dispose;
  }, []); // Listener hanya dipasang satu kali dan callback memakai ref terbaru

  const handleRemoveFromCart = (itemIndex) => {
    removeItemFromCart(itemIndex);
  };

  const handleUpdateQty = (itemIndex, delta, isAbsolute = false) => {
    const currentItem = cart[itemIndex];
    if (currentItem) {
      let newQuantity;
      if (isAbsolute) {
        newQuantity = Math.max(0.001, Math.round(Number(delta) * 1000) / 1000);
      } else {
        const cur = Number(currentItem.quantity || currentItem.qty || 0);
        const target = cur + delta;
        newQuantity = target <= 0 ? cur : Math.max(0.001, Math.round(target * 1000) / 1000);
      }
      updateItemQuantity(itemIndex, newQuantity);
    }
  };

  const normalizeCustomerName = (name) => {
    if (!name) return "";
    return name.trim().replace(/\s+/g, " ").toLowerCase();
  };

  const handleClearCart = () => {
    clearOrderCart();
    setCustomer(null);
    setWalkInName("");
    setSelectedTable(null);
    setSelectedUnit(null);
    setPendingOrder(null);
    setNotes("");
    if (defaultSalesType) setSalesType(defaultSalesType);
    try {
      localStorage.removeItem("cart_pos_active_draft");
      localStorage.removeItem("pos_active_cart_draft");
    } catch (e) {}
  };

  const fetchPendingCount = async () => {
    try {
      const res = await getSalesOrders({
        status: "PENDING",
        page: 1,
        limit: 1,
      });
      const total = Number(res?.meta?.total || 0);
      setPendingCount(Number.isFinite(total) ? total : 0);
    } catch {
      setPendingCount(0);
    }
  };

  const handleSaveDraft = async () => {
    if (cart.length === 0 || isSavingDraft) return;

    if (pendingOrder?.id) {
      await executeUpdateExistingPendingOrder(pendingOrder.id);
      return;
    }

    try {
      setIsSavingDraft(true);
      setLoadingPendingOrders(true);

      const activeCustName =
        customer?.name && customer.name.trim() !== "Pelanggan Umum"
          ? customer.name.trim()
          : walkInName && walkInName.trim() !== "Pelanggan Umum"
          ? walkInName.trim()
          : "";
      const searchCustName = activeCustName;
      const searchTableName = selectedTable?.name || "";

      // Opsi 1 (best practice POS) untuk pelanggan terdaftar / ber-nama / meja
      if (customer?.id || searchCustName || searchTableName) {
        const res = await getSalesOrders({
          status: "PENDING",
          customer_id: customer?.id || undefined,
          page: 1,
          limit: 100,
        });
        const orders = res?.data || [];
        const customerPendingOrders = orders.filter((order) => {
          const isPending = String(order?.status || "PENDING").toUpperCase() === "PENDING";
          if (!isPending) return false;

          const matchCustId = Boolean(customer?.id && String(order?.customer_id || "") === String(customer.id));
          const matchCustName = Boolean(
            searchCustName &&
            order?.customer_name &&
            normalizeCustomerName(order.customer_name) !== normalizeCustomerName("Pelanggan Umum") &&
            normalizeCustomerName(order.customer_name) === normalizeCustomerName(searchCustName)
          );
          const matchTable = Boolean(searchTableName && (order?.table_name === searchTableName || order?.selected_table_name === searchTableName));

          return matchCustId || matchCustName || matchTable;
        });

        // Pelanggan/Meja SUDAH punya transaksi pending → tampilkan pilihan
        if (customerPendingOrders.length > 0) {
          setActivePendingOrders(customerPendingOrders);
          setShowSelectOpenBillModal(true);
          return;
        }

        await executeCreateNewBill();
        return;
      }

      // Pelanggan umum (tanpa nama dari daftar pelanggan atau manual) → langsung simpan transaksi baru
      await executeCreateNewBill();
    } catch (err) {
      showError(
        err.response?.data?.message ||
          err.message ||
          "Gagal memuat daftar Open Bill aktif"
      );
    } finally {
      setLoadingPendingOrders(false);
      setIsSavingDraft(false);
    }
  };

  const triggerAutoPrintCartPending = async (
    cartItemsToPrint,
    targetCustomerName,
    targetTxNumber,
    isAdditional = false
  ) => {
    try {
      // Retail tidak memiliki nota pesanan atau label pesanan.
      if (localStorage.getItem("PREVIEW_APPS") !== "true") {
        return;
      }

      const storeRes = await configService.getStore().catch(() => null);
      const store = storeRes?.data || storeRes || {};
      const settings = store?.settings || {};

      if (!settings.auto_print_pending_enabled) {
        return;
      }

      if (!cartItemsToPrint || cartItemsToPrint.length === 0) return;

      const subtotalVal = cartItemsToPrint.reduce(
        (acc, item) => acc + calculateOrderItemTotals(item).subtotal,
        0,
      );

      const effectiveCustName =
        targetCustomerName ||
        customer?.name ||
        (walkInName && walkInName.trim()) ||
        "Pelanggan Umum";

      const receiptData = {
        storeName: store?.name || localStorage.getItem("tenantName") || "Toko",
        storeAddress: store?.settings?.address || store?.address || "",
        storePhone: store?.settings?.phone || store?.phone || "",
        storeLogoUrl: store?.logo_url || "",
        storeFooterNote: store?.settings?.footer_note || "",
        meta: {
          transactionNumber: targetTxNumber || ("DRAFT-" + new Date().getTime().toString().slice(-6)),
          transactionDateTime: new Date().toLocaleString("id-ID"),
          cashierName: getLoggedInUserName() || "-",
          customerName: effectiveCustName,
          sellerType: salesType?.name || salesType || "Internal",
          tableName: selectedTable?.name || pendingOrder?.table_name || "",
          unitName: selectedUnit?.name || selectedUnit?.unit_name || "",
          isAdditional,
          isAdditionalOrder: isAdditional,
          status: "NOTA PESANAN (DAPUR)",
        },
        items: cartItemsToPrint.map((item) => {
          const matchedProd = products.find(
            (p) => (p.product_id || p.id) === (item.product_id || item.id)
          );
          const resolvedCatId =
            item.category_id ||
            item.product?.category_id ||
            matchedProd?.category_id ||
            null;
          const resolvedCatName =
            item.category_name ||
            matchedProd?.category_name ||
            matchedProd?.category ||
            (typeof item.category === "string" ? item.category : null) ||
            null;

          return {
            name: item.product_name || item.item_name || item.name || "-",
            qty: Number(item.quantity || item.qty || 1),
            price: Number(item.unit_price || item.price || 0),
            total: calculateOrderItemTotals(item).subtotal,
            discount_amount: Number(item.discount_amount || 0),
            additional_cost: Number(item.additional_cost || 0),
            unit: item.unit?.name || item.asset_unit_name || item.unit_name || (typeof item.unit === "string" ? item.unit : ""),
            notes: item.notes || item.note || "",
            category_id: resolvedCatId,
            category_name: resolvedCatName,
            category: resolvedCatName,
            selected_options: item.selected_options || [],
            variant_name: item.variant_name || "",
          };
        }),
        totals: {
          subtotal: subtotalVal,
          discount: 0,
          additionalCosts: 0,
          total: subtotalVal,
          totalBayar: 0,
          isDownPayment: false,
          remaining: subtotalVal,
        },
      };

      await executePrint(receiptData, "order");

      // Auto Print Label Pesanan (jika aktif pada konfigurasi printer label)
      const printConfig = getReceiptConfig();
      if (printConfig?.orderLabelEnabled || printConfig?.autoPrintLabelEnabled) {
        const pad = (n) => String(n).padStart(2, "0");
        const now = new Date();
        const datetime = `${pad(now.getDate())}/${pad(now.getMonth() + 1)}/${String(now.getFullYear()).slice(2)} ${pad(now.getHours())}:${pad(now.getMinutes())}`;
        const storeName = store?.name || localStorage.getItem("tenantName") || "Toko";
        const salesTypeName = salesType?.name || (typeof salesType === "string" ? salesType : "") || "Internal";

        const labelFilteredItems = filterLabelItems(receiptData.items, printConfig?.labelCategories, printConfig?.labelCategoryNames);
        if (labelFilteredItems && labelFilteredItems.length > 0) {
          const expanded = [];
          labelFilteredItems.forEach((it) => {
            const qty = Number(it.quantity || it.qty || 1);
            const unit = it.unit?.name || it.asset_unit_name || it.unit_name || (typeof it.unit === "string" ? it.unit : "");
            const productName = it.product_name || it.item_name || it.name || "-";
            const variantName = it.variant_name || "";
            const itemNotes = it.notes || it.note || "";
            const addons = (it.selected_options || []).map((a) => ({
              name: a.name || a.item_name || "",
              qty: Number(a.qty || a.quantity || 1),
            }));
            for (let i = 0; i < qty; i++) {
              expanded.push({
                storeName,
                customerName: effectiveCustName,
                salesTypeName,
                productName,
                variantName,
                qty: 1,
                unit,
                notes: itemNotes,
                addons,
                datetime,
              });
            }
          });

          if (expanded.length > 0) {
            const total = expanded.length;
            const labelDataArray = expanded.map((e, idx) => ({ ...e, index: idx + 1, total }));
            await executePrintLabel(labelDataArray).catch((err) => {
              console.error("Auto print label draft error:", err);
            });
          }
        }
      }
    } catch (err) {
      console.error("Auto print cart draft error:", err);
    }
  };

  const executeCreateNewBill = async () => {
    try {
      setIsSavingDraft(true);
      const cartSnapshot = [...cart];
      const effectiveCustName =
        customer?.name || (walkInName && walkInName.trim()) || "Pelanggan Umum";

      const orderData = {
        customer_id: customer?.id || null,
        customer_name: effectiveCustName,
        seller_type: salesType?.name || salesType || "Internal",
        order_type_id: salesType?.id,
        table_id: selectedTable?.id || null,
        table_name: selectedTable?.name || "",
        items: cart.map((item) => {
          const isService = item.item_type === "SERVICE" || !!item.service_id;
          return {
            item_type: isService ? "SERVICE" : "PRODUCT",
            product_id: isService ? null : item.product_id,
            product_variant_id: item.product_variant_id,
            product_addon_id: item.product_addon_id,
            service_id: isService ? (item.service_id || item.product_id) : null,
            quantity: item.quantity,
            unit_id: item.unit_id,
            ...getSellingUnitPayload(item),
            unit_price: item.unit_price,
            discount_amount: Number(item.discount_amount || 0),
            additional_cost: Number(item.additional_cost || 0),
            notes: item.notes || "",
          };
        }),
        additional_costs: Number(additionalCosts || 0),
        discount_total: Number(discountTotal || 0),
        notes: notes || "",
      };

      const res = await createSalesOrder(orderData);
      const createdOrder = res?.data || res || {};
      showSuccess("Transaksi disimpan sebagai Open Bill Baru");
      setShowSelectOpenBillModal(false);

      await triggerAutoPrintCartPending(
        cartSnapshot,
        createdOrder?.customer_name || effectiveCustName,
        createdOrder?.transaction_number || createdOrder?.order_number,
        false
      );

      handleClearCart();
      fetchPendingCount();
      if (selectedCategory.id === "best_seller") {
        fetchBestSellerProducts(searchQuery, true);
      } else {
        fetchProducts(selectedCategory.id, searchQuery, true);
      }
    } catch (err) {
      showError(
        err.response?.data?.message || err.message || "Gagal menyimpan draft",
      );
    } finally {
      setIsSavingDraft(false);
    }
  };

  const calculateNewItemsForPendingUpdate = (currentCart, originalOrderItems) => {
    if (!Array.isArray(originalOrderItems) || originalOrderItems.length === 0) {
      return currentCart;
    }

    const getItemKey = (item) => {
      const prodId = String(item.product_id || item.id || "");
      const variantId = String(item.product_variant_id || item.variant_id || "");
      const addonId = String(item.product_addon_id || "");
      const serviceId = String(item.service_id || "");
      const unitId = String(item.unit_id || item.unit?.id || "");
      const itemNotes = String(item.notes || item.note || "").trim().toLowerCase();
      return `${prodId}|${variantId}|${addonId}|${serviceId}|${unitId}|${getSellingUnitKey(item)}|${itemNotes}`;
    };

    // Buat pool kuantiti item awal dari pending order
    const originalQtyPool = {};
    originalOrderItems.forEach((orig) => {
      const key = getItemKey(orig);
      originalQtyPool[key] = (originalQtyPool[key] || 0) + Number(orig.quantity || orig.qty || 1);
    });

    const newItemsToPrint = [];

    currentCart.forEach((item) => {
      const key = getItemKey(item);
      const currentQty = Number(item.quantity || item.qty || 1);
      const origQty = originalQtyPool[key] || 0;

      if (currentQty > origQty) {
        const addedQty = currentQty - origQty;
        newItemsToPrint.push({
          ...item,
          quantity: addedQty,
          qty: addedQty,
        });
        originalQtyPool[key] = 0;
      } else {
        originalQtyPool[key] = Math.max(0, origQty - currentQty);
      }
    });

    return newItemsToPrint;
  };

  const executeUpdateExistingPendingOrder = async (orderId) => {
    try {
      setIsSavingDraft(true);
      const cartSnapshot = [...cart];
      const effectiveCustName =
        customer?.name ||
        (walkInName && walkInName.trim()) ||
        pendingOrder?.customer_name ||
        "Pelanggan Umum";

      // Filter hanya produk baru / kuantiti tambahan saja yang dicetak ke dapur
      const originalItems = pendingOrder?.items || [];
      const newItemsToPrint = calculateNewItemsForPendingUpdate(cartSnapshot, originalItems);

      const orderData = {
        customer_id: customer?.id || null,
        customer_name: effectiveCustName,
        seller_type: salesType?.name || salesType || "Internal",
        order_type_id: salesType?.id,
        table_id: selectedTable?.id || pendingOrder?.table_id || null,
        table_name: selectedTable?.name || pendingOrder?.table_name || "",
        items: cart.map((item) => {
          const isService = item.item_type === "SERVICE" || !!item.service_id;
          return {
            item_type: isService ? "SERVICE" : "PRODUCT",
            product_id: isService ? null : item.product_id,
            product_variant_id: item.product_variant_id,
            product_addon_id: item.product_addon_id,
            service_id: isService ? (item.service_id || item.product_id) : null,
            quantity: item.quantity,
            unit_id: item.unit_id,
            ...getSellingUnitPayload(item),
            unit_price: item.unit_price,
            discount_amount: Number(item.discount_amount || 0),
            additional_cost: Number(item.additional_cost || 0),
            notes: item.notes || "",
          };
        }),
        additional_costs: Number(additionalCosts || 0),
        discount_total: Number(discountTotal || 0),
        notes: notes || "",
      };

      const updateRes = await updateSalesOrder(orderId, orderData);
      const updatedOrder = updateRes?.data || updateRes || {};
      showSuccess(`Open Bill ${updatedOrder?.transaction_number || pendingOrder?.transaction_number || ""} berhasil diperbarui`);
      setPendingOrder(null);
      setShowSelectOpenBillModal(false);

      const targetTxNumber = updatedOrder?.transaction_number || pendingOrder?.transaction_number;
      
      // Hanya lakukan auto print dapur jika memang ada produk baru / tambahan
      if (newItemsToPrint && newItemsToPrint.length > 0) {
        await triggerAutoPrintCartPending(
          newItemsToPrint,
          updatedOrder?.customer_name || pendingOrder?.customer_name || effectiveCustName,
          targetTxNumber,
          true
        );
      }

      handleClearCart();
      fetchPendingCount();
      if (selectedCategory.id === "best_seller") {
        fetchBestSellerProducts(searchQuery, true);
      } else {
        fetchProducts(selectedCategory.id, searchQuery, true);
      }
    } catch (err) {
      showError(
        err.response?.data?.message || err.message || "Gagal memperbarui draft",
      );
    } finally {
      setIsSavingDraft(false);
    }
  };

  const handleMergeToExistingBill = async (targetOrder) => {
    if (!targetOrder?.id || cart.length === 0) return;

    try {
      setIsSavingDraft(true);
      const cartSnapshot = [...cart];
      const res = await getSalesOrderDetail(targetOrder.id);
      const detail = res?.data || res || {};
      const existingItems = detail.items || [];

      const mappedExisting = existingItems.map((item) => ({
        item_type: item.item_type || "PRODUCT",
        product_id: item.product_id,
        product_variant_id: item.product_variant_id || null,
        product_addon_id: item.product_addon_id || null,
        service_id: item.service_id || null,
        quantity: Number(item.quantity || 1),
        unit_id: item.unit_id || null,
        ...getSellingUnitPayload(item),
        unit_price: Number(item.unit_price || item.price || 0),
        discount_amount: Number(item.discount_amount || 0),
        additional_cost: Number(item.additional_cost || 0),
        notes: item.notes || "",
      }));

      const mappedNew = cart.map((item) => ({
        item_type: item.item_type || "PRODUCT",
        product_id: item.product_id,
        product_variant_id: item.product_variant_id || null,
        product_addon_id: item.product_addon_id || null,
        service_id: item.service_id || null,
        quantity: item.quantity,
        unit_id: item.unit_id || null,
        ...getSellingUnitPayload(item),
        unit_price: item.unit_price,
        discount_amount: Number(item.discount_amount || 0),
        additional_cost: Number(item.additional_cost || 0),
        notes: item.notes || "",
      }));

      const combinedItems = [...mappedExisting, ...mappedNew];

      // Hitung ulang diskon / markup jika seller_type pada Open Bill tersebut memiliki persentase (misal Member 20%)
      const targetSellerType = salesTypes.find((type) => {
        const typeId = String(type?.id || "");
        const orderTypeId = String(detail?.order_type_id || "");
        const typeSlug = String(type?.slug || "").toUpperCase();
        const sellerTypeSlug = String(detail?.seller_type || "").toUpperCase();
        return (
          (typeId && orderTypeId && typeId === orderTypeId) ||
          (typeSlug && sellerTypeSlug && typeSlug === sellerTypeSlug)
        );
      });

      let updatedDiscountTotal = Number(detail.discount_total || 0);
      let updatedAdditionalCosts = Number(detail.additional_costs || 0);

      if (targetSellerType && targetSellerType.jenis) {
        const combinedSubtotal = combinedItems.reduce((sum, item) => sum + calculateOrderItemTotals(item).subtotal, 0);
        const numNilai = Number(targetSellerType.nilai) || 0;

        if (targetSellerType.jenis === 'Diskon') {
          updatedDiscountTotal = targetSellerType.satuan === '%' ? (combinedSubtotal * numNilai / 100) : numNilai;
        } else if (targetSellerType.jenis === 'Markup') {
          updatedAdditionalCosts = targetSellerType.satuan === '%' ? (combinedSubtotal * numNilai / 100) : numNilai;
        }
      }

      const effectiveCustName =
        (customer?.name && customer.name.trim()) ||
        (walkInName && walkInName.trim()) ||
        "Pelanggan Umum";
      const mergedCustomerName =
        detail.customer_name && detail.customer_name !== "Pelanggan Umum"
          ? detail.customer_name
          : effectiveCustName;

      const orderData = {
        customer_id: detail.customer_id || customer?.id || null,
        customer_name: mergedCustomerName,
        seller_type: detail.seller_type || salesType?.name || "Internal",
        order_type_id: detail.order_type_id || salesType?.id,
        table_id: detail.table_id || selectedTable?.id || null,
        table_name: detail.table_name || selectedTable?.name || "",
        items: combinedItems,
        additional_costs: updatedAdditionalCosts,
        discount_total: updatedDiscountTotal,
        notes: detail.notes || "",
      };

      const updateRes = await updateSalesOrder(targetOrder.id, orderData);
      const updatedOrder = updateRes?.data || updateRes || {};

      const targetTxNumber =
        updatedOrder?.transaction_number ||
        detail?.transaction_number ||
        targetOrder?.transaction_number;

      showSuccess(
        `Pesanan berhasil digabungkan ke Open Bill ${targetTxNumber || ""} (${detail.customer_name || targetOrder.customer_name || effectiveCustName})`,
      );

      setShowSelectOpenBillModal(false);

      await triggerAutoPrintCartPending(
        cartSnapshot,
        detail?.customer_name || targetOrder?.customer_name || effectiveCustName,
        targetTxNumber,
        true
      );

      handleClearCart();
      fetchPendingCount();
      if (selectedCategory.id === "best_seller") {
        fetchBestSellerProducts(searchQuery, true);
      } else {
        fetchProducts(selectedCategory.id, searchQuery, true);
      }
    } catch (err) {
      showError(
        err.response?.data?.message || err.message || "Gagal menggabungkan pesanan ke Open Bill",
      );
    } finally {
      setIsSavingDraft(false);
    }
  };

  const handleOpenShift = async (shiftData) => {
    try {
      await refreshActiveShift();
      showSuccess("Shift Dibuka", `Shift berhasil dibuka`);
      setShowShiftModal(false);
    } catch (error) {
      console.error("Error refreshing shift:", error);
      showError("Error", "Gagal memperbarui status shift");
    }
  };

  const handleCloseShift = async (closingData) => {
    try {
      await refreshActiveShift();
      showSuccess("Shift Ditutup", "Shift berhasil ditutup");
      setShowShiftModal(false);
    } catch (error) {
      console.error("Error refreshing shift:", error);
      showError("Error", "Gagal memperbarui status shift");
    }
  };

  // Handle category selection
  const handleSelectCategory = (cat) => {
    setSelectedCategory(cat);
    const isBS = cat.id === "best_seller";
    const cacheKey = isBS ? `best_seller_${searchQuery || ""}` : `${cat.id}_${searchQuery || ""}`;
    const cached = categoryProductsCacheRef.current[cacheKey];
    if (cached) {
      setProducts(cached);
    }
  };

  const handleResumePendingOrder = async (order) => {
    if (!order?.id) return;

    try {
      const res = await getSalesOrderDetail(order.id);
      const detail = res?.data ?? res ?? order;
      const detailItems = Array.isArray(detail?.items) ? detail.items : [];

      const mappedCart = detailItems.map((item, index) => {
        const isServiceItem = item.item_type === "SERVICE" || !!item.service_id;
        return {
          id:
            `pending-${item.product_id || item.service_id || index}-${item.product_variant_id || "base"}-${item.id || index}`,
          order_item_id: item.id || null,
          item_type: isServiceItem ? "SERVICE" : "PRODUCT",
          product_id: isServiceItem ? null : item.product_id,
          product_variant_id: item.product_variant_id || null,
          product_addon_id: item.product_addon_id || null,
          service_id: isServiceItem ? (item.service_id || item.product_id) : null,
        item_name:
          item.product_name ||
          item.service_name ||
          item.item_name ||
          item.name ||
          `Item ${index + 1}`,
        item_code:
          item.item_code ||
          item.product_code ||
          item.service_code ||
          `ITEM-${index + 1}`,
        quantity: Number(item.quantity || item.qty || 1),
        unit_price: Number(item.unit_price || item.price || 0),
        unit_id: item.unit_id || item.unit?.id || null,
        selling_unit_id: item.selling_unit_id || null,
        selling_unit_name: item.selling_unit_name || null,
        satuan: item.selling_unit_name || null,
        unit_name:
          item.unit?.name || item.asset_unit_name || item.unit_name || null,
        unit:
          item.unit?.id || item.unit?.name
            ? {
                id: item.unit?.id || item.unit_id || null,
                name:
                  item.unit?.name ||
                  item.asset_unit_name ||
                  item.unit_name ||
                  null,
              }
            : item.unit_id || item.asset_unit_name || item.unit_name
              ? {
                  id: item.unit_id || null,
                  name: item.asset_unit_name || item.unit_name || null,
                }
              : null,
        notes: item.notes || "",
          category_id: item.category_id || item.product?.category_id || item.category || null,
          discount_amount: Number(item.discount_amount || 0),
          additional_cost: Number(item.additional_cost || 0),
        };
    });

      const matchedSalesType = salesTypes.find((type) => {
        const typeId = String(type?.id || "");
        const orderTypeId = String(detail?.order_type_id || "");
        const typeSlug = String(type?.slug || "").toUpperCase();
        const sellerTypeSlug = String(detail?.seller_type || "").toUpperCase();
        return (
          (typeId && orderTypeId && typeId === orderTypeId) ||
          (typeSlug && sellerTypeSlug && typeSlug === sellerTypeSlug)
        );
      });

      replaceCart(mappedCart);

      // Deteksi dan pulihkan unit kendaraan transaksi jika ada pada item pending
      const foundUnitItem = mappedCart.find((it) => it.unit || it.unit_name || it.unit_id);
      if (foundUnitItem) {
        setSelectedUnit(
          foundUnitItem.unit || {
            id: foundUnitItem.unit_id,
            name: foundUnitItem.unit_name,
          }
        );
      } else {
        setSelectedUnit(null);
      }

      if (detail?.customer_id) {
        setCustomer({
          id: detail.customer_id,
          name: detail.customer_name || "Pelanggan",
          phone: detail.customer_phone || "",
          phone_number: detail.customer_phone || "",
          address: detail.customer_address || "",
        });
        setWalkInName("");
      } else {
        setCustomer(null);
        setWalkInName(
          detail?.customer_name && detail.customer_name !== "Pelanggan Umum"
            ? detail.customer_name
            : ""
        );
      }
      setSalesType(matchedSalesType || salesType);
      setAdditionalCosts(Number(detail?.additional_costs || 0));
      setDiscountTotal(Number(detail?.discount_total || 0));
      setNotes(detail?.notes || "");
      setSelectedTable(
        detail?.table_id
          ? { id: detail.table_id, name: detail.table_name || "Meja" }
          : null,
      );
      setPendingOrder(detail);
      setShowPendingModal(false);
      showSuccess(
        `Transaksi ${detail?.transaction_number || ""} dimuat ke keranjang`,
      );
    } catch (err) {
      showError(
        err?.response?.data?.message ||
          err?.message ||
          "Gagal memuat transaksi pending",
      );
    }
  };

  // Use totals from useOrderCreation hook
  const subtotal = totals.subtotal;
  const total = totals.grandTotal;

  // API memfilter data, lalu frontend memastikan semua field pencarian tetap cocok.
  const normalizedSearch = searchQuery.trim().toLowerCase();
  const filteredProducts = normalizedSearch
    ? products.filter((product) =>
        [
          product.name,
          product.code,
          product.barcode,
          product.sku,
          product.variant_name,
          product.unit,
        ].some((value) =>
          String(value || "").toLowerCase().includes(normalizedSearch),
        ),
      )
    : products;

  return (
    <div className="flex h-screen overflow-hidden bg-slate-50">
      <Sidebar
        isCollapsed={isSidebarCollapsed}
        onToggle={() =>
          setIsSidebarCollapsed((prev) => {
            const newState = !prev;
            localStorage.setItem("sidebarCollapsed", JSON.stringify(newState));
            return newState;
          })
        }
        activePage="penjualan"
        isDrawerOnly={categoryPosition === "side"}
      />

      <div
        className={`flex-1 transition-all duration-300 ${categoryPosition === "side" ? "ml-0" : isSidebarCollapsed ? "ml-16" : "ml-48"} flex flex-col overflow-hidden`}>
        {/* Top Header */}
        <header className="bg-white/85 backdrop-blur-md border-b border-slate-200/70 shrink-0 flex flex-col md:flex-row items-stretch min-h-14 sm:min-h-16 h-auto md:h-16">
          {/* Kolom Header Kiri: Sejajar dengan area Katalog / CategoryFilter */}
          <div className="flex-1 flex items-center justify-between gap-2 sm:gap-3 px-3 sm:px-6 min-w-0 h-14 sm:h-16 md:h-auto">
            {/* Kiri: burger + toko */}
            <div className="flex items-center gap-2 min-w-0 flex-1 pr-2">
              {categoryPosition === "side" ? (
                <button
                  type="button"
                  onClick={toggleMobile}
                  className="p-2 -ml-2 rounded-xl text-slate-600 hover:bg-slate-100 hover:text-teal-700 transition-colors flex-shrink-0 cursor-pointer"
                  title="Menu Utama"
                  aria-label="Buka menu navigasi">
                  <Menu className="w-5 h-5" />
                </button>
              ) : sidebarConfig.mobileHide ? (
                <button
                  type="button"
                  onClick={toggleMobile}
                  className="md:hidden p-2 -ml-2 rounded-xl text-slate-600 hover:bg-slate-100 hover:text-teal-700 transition-colors flex-shrink-0 cursor-pointer"
                  title="Menu Utama"
                  aria-label="Buka menu navigasi">
                  <Menu className="w-5 h-5" />
                </button>
              ) : null}
              <div className="min-w-0 flex-1">
                <StoreSelector
                  onSwitch={() => {
                    if (selectedCategory.id === "best_seller") {
                      fetchBestSellerProducts(searchQuery);
                    } else {
                      fetchProducts(selectedCategory.id, searchQuery);
                    }
                    fetchPendingCount();
                  }}
                />
              </div>
            </div>

            {/* Search bar with the compact scanner action - batas kanan sejajar dengan batas kanan CategoryFilter */}
            <div className="hidden sm:flex items-center gap-2 relative w-full sm:w-64 md:w-72 lg:w-80 xl:w-96 shrink-0 min-w-0 ml-auto">
              <div className="relative flex-1 min-w-0">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  placeholder="Cari produk / barcode..."
                  data-barcode-scanner="true"
                  className="w-full pl-9 pr-9 py-1.5 sm:py-2 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-500 focus:bg-white transition-colors text-xs sm:text-sm font-medium"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                {!isApps && (
                  <button
                    type="button"
                    onClick={handleOpenScanner}
                    title={
                      !scannerReady
                        ? "Konfigurasi scanner"
                        : activeScannerMode === "capacitor_apk"
                          ? "Buka kamera scanner"
                          : "Scanner siap digunakan"
                    }
                    aria-label="Scan barcode"
                    className={`absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1 transition-colors ${
                      scannerReady
                        ? "text-emerald-600 hover:bg-emerald-50"
                        : "text-amber-600 hover:bg-amber-50"
                    }`}
                  >
                    <QrCode className="h-4 w-4" />
                    <span
                      className={`absolute right-0 top-0 h-1.5 w-1.5 rounded-full ring-2 ring-slate-50 ${
                        scannerReady ? "bg-emerald-500" : "bg-amber-500"
                      }`}
                    />
                  </button>
                )}
              </div>
            </div>

            {/* Mobile Action Icons (<768px saja) */}
            <div className="md:hidden flex items-center gap-1.5 shrink-0">
              <button
                onClick={() => {
                  if (canProcessTransaction && activeShift) {
                    navigate("/shift-kasir");
                  } else {
                    setShowShiftModal(true);
                  }
                }}
                className={`p-2 rounded-lg transition-colors ${
                  canProcessTransaction
                    ? "bg-emerald-50 text-emerald-700"
                    : "bg-rose-50 text-rose-700"
                }`}>
                {canProcessTransaction ? <Unlock className="w-4 h-4" /> : <Lock className="w-4 h-4" />}
              </button>
              <button
                onClick={() => setShowPendingModal(true)}
                className="p-2 bg-amber-50 text-amber-700 rounded-lg relative">
                <History className="w-4 h-4" />
                {pendingCount > 0 && (
                  <span className="absolute -top-1 -right-1 min-w-4 h-4 px-1 bg-amber-600 text-white text-[10px] rounded-full flex items-center justify-center font-bold">
                    {pendingCount}
                  </span>
                )}
              </button>

              {/* Profile Button Mobile */}
              <div className="relative shrink-0" ref={profileMenuRef}>
                <button
                  ref={profileButtonRef}
                  onClick={handleProfileToggle}
                  className="flex items-center justify-center rounded-full hover:bg-slate-100 p-0.5 transition-colors">
                  <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-gradient-to-br from-teal-500 to-teal-700 overflow-hidden flex items-center justify-center ring-2 ring-white shadow-sm">
                    {profileIdentity.avatarUrl ? (
                      <img
                        src={profileIdentity.avatarUrl}
                        alt="Avatar"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <User className="w-3.5 h-3.5 text-white" />
                    )}
                  </div>
                </button>
              </div>
            </div>
          </div>

          {/* Kolom Header Kanan: Batas kiri tombol Shift mentok lurus Cart, batas kanan avatar sejajar Icon Meja */}
          <div className="hidden md:flex items-center gap-3 md:gap-3.5 lg:gap-4 md:w-[320px] lg:w-[360px] 2xl:w-[460px] shrink-0 pl-0 pr-4 justify-start">
            <button
              onClick={() => {
                if (canProcessTransaction && activeShift) {
                  navigate("/shift-kasir");
                } else {
                  setShowShiftModal(true);
                }
              }}
              className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-xl transition-colors font-medium text-xs sm:text-sm whitespace-nowrap shadow-sm ${
                canProcessTransaction
                  ? "bg-emerald-50 border border-emerald-200 text-emerald-700 hover:bg-emerald-100"
                  : "bg-rose-50 border border-rose-200 text-rose-700 hover:bg-rose-100"
              }`}>
              {canProcessTransaction ? (
                <Unlock className="w-4 h-4 shrink-0" />
              ) : (
                <Lock className="w-4 h-4 shrink-0" />
              )}
              <span>
                {shiftLoading
                  ? "Loading..."
                  : canProcessTransaction
                    ? "Shift Aktif"
                    : "Buka Shift"}
              </span>
            </button>

            <button
              onClick={() => setShowPendingModal(true)}
              className="flex-1 flex items-center justify-center gap-1.5 sm:gap-2 py-2 px-2.5 sm:px-3 bg-amber-50 border border-amber-200 text-amber-700 rounded-xl hover:bg-amber-100 transition-colors font-medium text-xs sm:text-sm relative whitespace-nowrap shadow-sm">
              <History className="w-4 h-4 shrink-0" />
              <span className="hidden lg:inline">Transaksi </span>
              <span>Pending</span>
              {pendingCount > 0 && (
                <span className="absolute -top-2 -right-2 w-5 h-5 bg-amber-600 text-white text-[11px] font-bold rounded-full flex items-center justify-center leading-none ring-2 ring-white shadow-sm">
                  {pendingCount}
                </span>
              )}
            </button>

            <div className="relative shrink-0" ref={desktopProfileMenuRef}>
              <button
                ref={desktopProfileButtonRef}
                onClick={handleProfileToggle}
                className="flex items-center justify-center rounded-full hover:bg-slate-100 p-0 transition-colors">
                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-teal-500 to-teal-700 overflow-hidden flex items-center justify-center ring-2 ring-white shadow-sm">
                  {profileIdentity.avatarUrl ? (
                    <img
                      src={profileIdentity.avatarUrl}
                      alt="Avatar"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <User className="w-4 h-4 text-white" />
                  )}
                </div>
              </button>
            </div>
          </div>

          {/* Search mobile (full width di baris bawah) */}
          <div className="px-3 pb-2.5 sm:hidden">
            <div className="relative">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="Cari produk disini..."
                className="w-full pl-10 pr-10 py-2 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-500 focus:bg-white transition-colors text-xs"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {!isApps && (
                <button
                  type="button"
                  onClick={handleOpenScanner}
                  title={scannerReady ? "Scanner siap digunakan" : "Konfigurasi scanner"}
                  aria-label="Scan barcode"
                  className={`absolute right-2.5 top-1/2 -translate-y-1/2 rounded-md p-1 transition-colors ${
                    scannerReady
                      ? "text-emerald-600 hover:bg-emerald-50"
                      : "text-amber-600 hover:bg-amber-50"
                  }`}>
                  <QrCode className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        </header>

        {showProfileMenu &&
          createPortal(
            <div
              ref={profilePanelRef}
              style={{
                position: "fixed",
                top: profileMenuCoords.top,
                right: profileMenuCoords.right,
              }}
              className="w-60 bg-white rounded-xl shadow-lg ring-1 ring-slate-200/70 py-1 z-[1000]">
              <div className="px-4 py-3 border-b border-slate-100">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-teal-500 to-teal-700 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden">
                    {profileIdentity.avatarUrl ? (
                      <img
                        src={profileIdentity.avatarUrl}
                        alt="Avatar"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <User className="w-5 h-5 text-white" />
                    )}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-slate-800 truncate">
                      {profileIdentity.username}
                    </p>
                    {profileIdentity.email && (
                      <p className="text-xs text-slate-500 truncate">
                        {profileIdentity.email}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              <div className="py-1">
                <button
                  onClick={handleProfileClick}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors">
                  <User className="w-4 h-4 text-slate-400" />
                  Profile
                </button>
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-rose-600 hover:bg-rose-50 transition-colors">
                  <LogOut className="w-4 h-4" />
                  Logout
                </button>
              </div>
            </div>,
            document.body,
          )}

        {/* Content Area */}
        <div className="flex flex-1 overflow-hidden">
          {/* Main Content (Product List) */}
          <div className="flex-1 flex flex-col bg-slate-50 p-3 sm:p-6 overflow-hidden">
            {/* Toolbar Right Controls (ViewMode Grid/List + Category Layout Top/Side Switcher) */}
            {(() => {
              const toolbarControls = (
                <div className="flex items-center gap-1.5 shrink-0">
                  {/* [FLAG_LEGIUN] Grid / List Switcher - Disembunyikan untuk Legiun Diesel */}
                  {!HIDE_GRID_MODE && (
                    <div className="flex items-center gap-0.5 bg-slate-50 p-1 rounded-lg border border-slate-100 shrink-0">
                      <button
                        type="button"
                        onClick={() => {
                          setViewMode("grid");
                          localStorage.setItem("catalog_view_mode", "grid");
                        }}
                        className={`p-1.5 rounded-md transition-colors ${
                          viewMode === "grid" 
                            ? "bg-white text-teal-600 shadow-sm ring-1 ring-slate-200" 
                            : "text-slate-400 hover:text-slate-600 hover:bg-white/50"
                        }`}
                        title="Tampilan Grid (Kotak)"
                      >
                        <LayoutGrid className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setViewMode("list");
                          localStorage.setItem("catalog_view_mode", "list");
                        }}
                        className={`p-1.5 rounded-md transition-colors ${
                          viewMode === "list" 
                            ? "bg-white text-teal-600 shadow-sm ring-1 ring-slate-200" 
                            : "text-slate-400 hover:text-slate-600 hover:bg-white/50"
                        }`}
                        title="Tampilan List (Daftar)"
                      >
                        <List className="w-4 h-4" />
                      </button>
                    </div>
                  )}

                  {/* Top / Side Category Position Switcher */}
                  <div className="flex items-center gap-0.5 bg-slate-50 p-1 rounded-lg border border-slate-100 shrink-0">
                    <button
                      type="button"
                      onClick={() => {
                        const nextPos = categoryPosition === "side" ? "top" : "side";
                        setCategoryPosition(nextPos);
                        localStorage.setItem("catalog_category_position", nextPos);
                        showSuccess(`Tata letak kategori: ${nextPos === "side" ? "Mode Samping Kiri (Vertikal)" : "Mode Atas (Horizontal)"}`);
                      }}
                      className={`p-1.5 rounded-md transition-colors cursor-pointer ${
                        categoryPosition === "side" 
                          ? "bg-white text-teal-600 shadow-sm ring-1 ring-slate-200" 
                          : "text-slate-400 hover:text-slate-600 hover:bg-white/50"
                      }`}
                      title={`Ubah tata letak kategori ke ${categoryPosition === "side" ? "Mode Atas (Horizontal)" : "Mode Samping Kiri (Vertikal)"}`}
                    >
                      {categoryPosition === "side" ? (
                        <PanelLeft className="w-4 h-4 text-teal-600" />
                      ) : (
                        <PanelTop className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                </div>
              );

              const productContent = (
                <>
                  {/* Loading State */}
                  {productsLoading && (
                    <div className="flex-1 flex items-center justify-center">
                      <div className="text-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto"></div>
                        <p className="text-gray-500 mt-4">Memuat produk...</p>
                      </div>
                    </div>
                  )}

                  {/* Error State */}
                  {productsError && (
                    <div className="flex-1 flex items-center justify-center">
                      <div className="text-center">
                        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                          <p className="text-red-600 mb-4">{productsError}</p>
                          <button
                            onClick={() =>
                              fetchProducts(selectedCategory.id, searchQuery)
                            }
                            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors">
                            Coba Lagi
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Product List */}
                  {!productsError && (
                    <div className="flex-1 flex flex-col min-h-0">
                      <div className="flex-1 overflow-y-auto">
                        {/* [FLAG_LEGIUN] Jika HIDE_GRID_MODE aktif, selalu tampilkan ProductList */}
                        {!HIDE_GRID_MODE && viewMode === "grid" ? (
                          <ProductGrid
                            products={filteredProducts}
                            onAddToCart={triggerAddToCart}
                            loading={productsLoading}
                            isBestSellerView={selectedCategory.id === "best_seller"}
                            isManualBestSeller={bestSellerConfig?.mode === "manual"}
                            onRemoveBestSeller={handleRemoveManualBestSeller}
                            emptyTitle={selectedCategory.id === "best_seller" ? "Belum ada produk Best Seller" : undefined}
                            emptyMessage={selectedCategory.id === "best_seller" ? "Produk Best Seller akan muncul setelah ada minimal beberapa transaksi" : undefined}
                            showStock={showProductStock}
                          />
                        ) : (
                          <ProductList
                            products={filteredProducts}
                            onAddToCart={triggerAddToCart}
                            loading={productsLoading}
                            isBestSellerView={selectedCategory.id === "best_seller"}
                            isManualBestSeller={bestSellerConfig?.mode === "manual"}
                            onRemoveBestSeller={handleRemoveManualBestSeller}
                            emptyTitle={selectedCategory.id === "best_seller" ? "Belum ada produk Best Seller" : undefined}
                            emptyMessage={selectedCategory.id === "best_seller" ? "Produk Best Seller akan muncul setelah ada minimal beberapa transaksi" : undefined}
                            showImage={HIDE_PRODUCT_IMAGE ? false : showListImage}
                            showStock={showProductStock}
                          />
                        )}

                        {selectedCategory.id === "best_seller" && (bestSellerConfig?.mode === "manual" || bestSellerConfig === null) && (
                          <div className="mt-6 flex justify-start">
                            <button
                              onClick={() => setIsBestSellerModalOpen(true)}
                              className="px-4 py-2.5 bg-amber-50 text-amber-700 hover:bg-amber-100 font-semibold rounded-xl text-sm transition-colors border border-amber-200 shadow-sm flex items-center gap-2">
                              <Plus className="w-4 h-4" />
                              Tambah Produk Best Seller
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </>
              );

              if (categoryPosition === "side") {
                return (
                  <div className="flex-1 flex flex-row gap-3 sm:gap-5 overflow-hidden min-h-0">
                    {/* Category Sidebar (Mode Samping Kiri ala Majoo POS) */}
                    <CategoryFilter
                      orientation="vertical"
                      categories={categories}
                      selected={selectedCategory.id}
                      onSelect={handleSelectCategory}
                    />

                    {/* Product Main Content with Top Header Bar */}
                    <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
                      {/* Sub-header Produk di Atas Grid/List */}
                      <div className="flex items-center justify-between mb-3 shrink-0 bg-white px-4 py-2 rounded-xl ring-1 ring-slate-200/70 shadow-2xs">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="font-bold text-sm text-slate-800 truncate">
                            {selectedCategory.name}
                          </span>
                          <span className="text-xs text-slate-400 font-medium shrink-0">
                            ({filteredProducts.length} produk)
                          </span>
                        </div>
                        {toolbarControls}
                      </div>

                      {/* Konten Produk */}
                      {productContent}
                    </div>
                  </div>
                );
              }

              // Default: Mode Atas (Horizontal)
              return (
                <>
                  <div className="mb-4 sm:mb-6 shrink-0">
                    <CategoryFilter
                      orientation="horizontal"
                      categories={categories}
                      selected={selectedCategory.id}
                      onSelect={handleSelectCategory}
                      rightContent={toolbarControls}
                    />
                  </div>
                  {productContent}
                </>
              );
            })()}
          </div>

          {/* Backdrop (mobile <768px saja) saat keranjang terbuka */}
          {cartOpen && (
            <div
              onClick={() => setCartOpen(false)}
              className="md:hidden fixed inset-0 top-16 bg-slate-900/30 z-30"
              aria-hidden="true"
            />
          )}

          {/* Right Sidebar (Cart) - drawer yang bisa dilipat di mobile (<768px), selalu terbuka di tablet & desktop (>=768px) */}
          <div
            className={`bg-white border-l shadow-xl flex flex-col z-40 overflow-hidden
              fixed top-16 bottom-0 right-0 w-[88%] max-w-[480px]
              transition-transform duration-300 ease-in-out
              md:static md:top-auto md:bottom-auto md:right-auto md:w-[320px] lg:w-[360px] 2xl:w-[460px] md:max-w-none md:z-20 md:translate-x-0 md:transition-none
              ${cartOpen ? "translate-x-0" : "translate-x-full"}`}>
            <CartSidebar
              cart={cart.map((item) => ({
                id:
                  item.cart_id ||
                  item.id ||
                  `${item.item_type || "PRODUCT"}-${item.product_id || item.service_id || "item"}-${item.product_variant_id || "base"}`,
                name: item.item_name || item.name,
                price: item.unit_price || item.price,
                qty: item.quantity || item.qty,
                unit: item.unit,
                unit_id: item.unit_id,
                unit_name: item.unit_name,
                variant_name: item.variant_name,
                selected_options: item.selected_options || [],
                product_id: item.product_id,
                product_variant_id: item.product_variant_id,
              }))}
              customer={customer}
              salesType={salesType}
              walkInName={walkInName}
              onWalkInNameChange={setWalkInName}
              onSelectCustomer={() => setShowCustomerModal(true)}
              onClearCustomer={() => setCustomer(null)}
              selectedTable={selectedTable}
              onSelectTable={() => setShowSelectTableModal(true)}
              onUnselectTable={() => setSelectedTable(null)}
              selectedUnit={selectedUnit}
              onSelectTransactionUnit={handleSelectTransactionUnit}
              onClearTransactionUnit={handleClearTransactionUnit}
              onSelectUnit={(cartItemId, selectedUnit) => {
                const itemIndex = typeof cartItemId === "number"
                  ? cartItemId
                  : cart.findIndex(
                      (item) =>
                        (item.cart_id ||
                          item.id ||
                          `${item.item_type || "PRODUCT"}-${item.product_id || item.service_id || "item"}-${item.product_variant_id || "base"}`) ===
                        cartItemId,
                    );
                if (itemIndex !== -1 && itemIndex < cart.length) {
                  updateItemUnit(itemIndex, selectedUnit);
                }
              }}
              onChangeSalesType={() => setShowSalesTypeModal(true)}
              onRemove={(cartItemId) => {
                const itemIndex = typeof cartItemId === "number" ? cartItemId : cart.findIndex(
                  (item) =>
                    (item.cart_id ||
                      item.id ||
                      `${item.item_type || "PRODUCT"}-${item.product_id || item.service_id || "item"}-${item.product_variant_id || "base"}`) ===
                    cartItemId,
                );
                if (itemIndex !== -1) {
                  handleRemoveFromCart(itemIndex);
                }
              }}
              onUpdateQty={(cartItemId, delta, isAbsolute = false) => {
                const itemIndex = typeof cartItemId === "number" ? cartItemId : cart.findIndex(
                  (item) =>
                    (item.cart_id ||
                      item.id ||
                      `${item.item_type || "PRODUCT"}-${item.product_id || item.service_id || "item"}-${item.product_variant_id || "base"}`) ===
                    cartItemId,
                );
                if (itemIndex !== -1) {
                  handleUpdateQty(itemIndex, delta, isAbsolute);
                }
              }}
              onUpdateItemNotes={(itemIndex, itemNotes) => {
                const targetIdx = typeof itemIndex === "number" ? itemIndex : cart.findIndex(
                  (item) =>
                    (item.cart_id ||
                      item.id ||
                      `${item.item_type || "PRODUCT"}-${item.product_id || item.service_id || "item"}-${item.product_variant_id || "base"}`) ===
                    itemIndex,
                );
                if (targetIdx !== -1) {
                  updateItemNotes(targetIdx, itemNotes);
                }
              }}
              onUpdateItemPrice={(itemIndex, newPrice, priceObj) => {
                const targetIdx = typeof itemIndex === "number" ? itemIndex : cart.findIndex(
                  (item) =>
                    (item.cart_id ||
                      item.id ||
                      `${item.item_type || "PRODUCT"}-${item.product_id || item.service_id || "item"}-${item.product_variant_id || "base"}`) ===
                    itemIndex,
                );
                if (targetIdx !== -1) {
                  updateItemPrice(targetIdx, newPrice, priceObj);
                }
              }}
              onClear={() => {
                handleClearCart();
                setSelectedTable(null);
              }}
              onPay={() => {
                if (cart.length > 0) {
                  setShowPaymentModal(true);
                }
              }}
              onSaveDraft={handleSaveDraft}
              isSavingDraft={isSavingDraft}
              subtotal={subtotal}
              total={total}
              additionalCosts={additionalCosts}
              discountTotal={discountTotal}
              dpAmount={pendingOrder?.total_paid || 0}
            />
          </div>

          {/* FAB toggle keranjang (mobile <768px saja) */}
          <button
            onClick={() => setCartOpen((prev) => !prev)}
            className="md:hidden fixed right-0 top-1/2 -translate-y-1/2 z-50
              w-9 h-16 bg-teal-600 text-white rounded-l-xl shadow-lg
              flex items-center justify-center hover:bg-teal-700 active:scale-95 transition-all"
            title={cartOpen ? "Tutup keranjang" : "Buka keranjang"}
            aria-label={cartOpen ? "Tutup keranjang" : "Buka keranjang"}>
            {cartOpen ? (
              <ChevronRight className="w-5 h-5" />
            ) : (
              <ChevronLeft className="w-5 h-5" />
            )}
          </button>
        </div>
      </div>

      {/* Modals */}
      {showSalesTypeModal && (
        <SalesTypeModal
          salesTypes={salesTypes}
          onSelect={(type) => {
            setSalesType(type); // Store full object with name and slug
            setShowSalesTypeModal(false);
          }}
          onClose={() => setShowSalesTypeModal(false)}
        />
      )}

      {showCustomerModal && (
        <CustomerModal
          onClose={() => setShowCustomerModal(false)}
          onSelect={(cust) => {
            setCustomer(cust);
            setShowCustomerModal(false);
          }}
        />
      )}

      {showSelectTableModal && (
        <SelectTableModal
          isOpen={showSelectTableModal}
          onClose={() => setShowSelectTableModal(false)}
          selectedTable={selectedTable}
          customer={customer}
          walkInName={walkInName}
          onSelectTable={(table) => {
            setSelectedTable(table);
            setShowSelectTableModal(false);
          }}
        />
      )}

      {showPaymentModal && (
        <EnhancedPaymentModal
          cart={cart}
          customer={customer}
          walkInName={walkInName}
          sellerType={salesType}
          salesTypes={salesTypes}
          selectedTable={selectedTable}
          selectedUnit={selectedUnit}
          additionalCosts={additionalCosts}
          discountTotal={discountTotal}
          pendingOrder={pendingOrder}
          notes={notes}
          onOrderCreated={(createdOrder) => {
            // Otomatis kembalikan status meja yang terhubung ke Kosong (AVAILABLE) saat transaksi selesai (Lunas)
            if (selectedTable?.id) {
              masterService.tables.updateStatus(selectedTable.id, {
                status: "AVAILABLE",
                notes: "",
                reserved_by: "",
              }).catch((err) => console.error("Gagal memperbarui status meja otomatis:", err));
            }

            // Langsung kurangi stok produk yang terjual di cache memori seketika transaksi berhasil dibuat di server
            const itemsToDeduct = (cart && cart.length > 0) ? [...cart] : (createdOrder?.items || []);
            if (itemsToDeduct && itemsToDeduct.length > 0) {
              Object.keys(categoryProductsCacheRef.current).forEach((key) => {
                categoryProductsCacheRef.current[key] = (categoryProductsCacheRef.current[key] || []).map((p) => {
                  const soldItems = itemsToDeduct.filter((c) => c.product_id === p.product_id || c.id === p.id || (p.product_id && c.product_id === p.product_id));
                  if (soldItems.length > 0 && typeof p.stock === "number") {
                    const totalDeduction = soldItems.reduce((sum, item) => {
                      const factor = Number(item.unit_factor || item.factor || 1) || 1;
                      return sum + ((item.quantity || 1) * factor);
                    }, 0);
                    return { ...p, stock: Math.max(0, p.stock - totalDeduction) };
                  }
                  return p;
                });
              });
            }

            // Langsung bersihkan keranjang seketika order berhasil dibuat di server
            handleClearCart();
            setWalkInName("");
            setSelectedTable(null);
            setPendingOrder(null);
          }}
          onClose={() => {
            setShowPaymentModal(false);
          }}
          onSuccess={(createdOrder) => {
            showSuccess(
              `Transaksi ${createdOrder?.transaction_number || ""} berhasil dibuat`,
            );
            setShowPaymentModal(false);
            setPendingOrder(null);
            
            // Bersihkan kembali untuk memastikan
            handleClearCart();
            setWalkInName("");
            setSelectedTable(null);

            if (selectedCategory.id === "best_seller") {
              fetchBestSellerProducts(searchQuery, true);
            } else {
              fetchProducts(selectedCategory.id, searchQuery, true);
            }
            fetchPendingCount();
          }}
        />
      )}

      {showShiftModal && (
        <ShiftModal
          isOpen={showShiftModal}
          onClose={() => setShowShiftModal(false)}
          onOpenShift={handleOpenShift}
          onCloseShift={handleCloseShift}
        />
      )}

      <OptionSelectionModal
        isOpen={optionModalOpen}
        onClose={() => {
          setOptionModalOpen(false);
          setOptionProduct(null);
        }}
        product={optionProduct}
        onConfirm={handleAddToCart}
        showStock={showProductStock}
      />

      <PreCheckoutHookModal
        isOpen={showPreCheckoutModal}
        onClose={() => setShowPreCheckoutModal(false)}
        outOfStockItems={outOfStockItems}
        isAutoConversionEnabled={isAutoConversionEnabled}
        onProceed={() => {
          setShowPreCheckoutModal(false);
          setShowPaymentModal(true);
        }}
      />

      {showPendingModal && (
        <PendingTransactionsModal
          isOpen={showPendingModal}
          onClose={() => {
            setShowPendingModal(false);
            fetchPendingCount();
            if (selectedCategory?.id === "best_seller") {
              fetchBestSellerProducts(searchQuery, true);
            } else if (selectedCategory?.id) {
              fetchProducts(selectedCategory.id, searchQuery, true);
            }
          }}
          onOrderCancelled={() => {
            fetchPendingCount();
            if (selectedCategory?.id === "best_seller") {
              fetchBestSellerProducts(searchQuery, true);
            } else if (selectedCategory?.id) {
              fetchProducts(selectedCategory.id, searchQuery, true);
            }
          }}
          onResume={handleResumePendingOrder}
        />
      )}
      
      <SelectBestSellerModal
        isOpen={isBestSellerModalOpen}
        onClose={() => setIsBestSellerModalOpen(false)}
        currentBestSellers={bestSellerConfig?.manual_product_ids || []}
        onSelect={handleAddManualBestSeller}
        onRemove={handleRemoveManualBestSeller}
      />

      <SelectOpenBillModal
        isOpen={showSelectOpenBillModal}
        onClose={() => setShowSelectOpenBillModal(false)}
        cartItemsCount={cart.length}
        pendingOrders={activePendingOrders}
        loadingOrders={loadingPendingOrders}
        salesTypes={salesTypes}
        onSaveNewBill={executeCreateNewBill}
        onMergeToExistingBill={handleMergeToExistingBill}
      />

      <BarcodeScannerModal
        isOpen={showCameraScanner}
        onClose={() => setShowCameraScanner(false)}
        onDetected={handleBarcodeScan}
      />
    </div>
  );
}
